import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Input,
  Button,
  SimpleGrid,
  Icon,
  HStack,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  Flex,
  Image,
  Grid,
  GridItem,
  keyframes,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  CircularProgress,
  CircularProgressLabel,
  Checkbox,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaRocket,
  FaChartBar,
  FaTrophy,
  FaQuestionCircle,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaMapMarkerAlt,
  FaBriefcase,
  FaLock,
  FaBullhorn,
  FaCommentDots,
  FaGlobe,
  FaBrain,
  FaChartLine,
  FaCity,
  FaTags,
  FaExclamationTriangle,
  FaStar,
  FaQuoteLeft,
  FaArrowDown,
} from 'react-icons/fa';

import AnimatedBox from './AnimatedBox';
import AutocompleteInput from './AutocompleteInput';
import LandingCompetitorMap from './LandingCompetitorMap';
import LandingDashboardShowcase from './LandingDashboardShowcase';
import { createLandingCheckoutSession } from '../services/billingService';
import { submitLandingForm } from '../services/landingService';
import { getRateLimitService } from '../services/rateLimitService';
import { searchCities } from '../data/usCities';
import { searchBusinessTypes } from '../data/businessTypes';

export const SMS_CONSENT_TEXT =
  'I agree to receive my market report and related updates by text message from Localytics at the number above. Message and data rates may apply. Reply STOP to opt out.';

const REPORT_PRICE_USD = 297;
// Anchor / list price the Founder's Beta discount is measured against.
// Display-only; checkout still runs at REPORT_PRICE_USD.
const REPORT_LIST_PRICE_USD = 497;
// Add-on for a second city or second niche (comparison dashboard).
const SECOND_MARKET_ADDON_USD = 197;
// Hard cap on the Founder's Beta cohort. Drives scarcity copy on the
// pricing card. Lower this manually as we approach the cap.
const FOUNDER_BETA_SEATS_TOTAL = 50;
const FOUNDER_BETA_SEATS_REMAINING = 37;
// Delivery window for the private dashboard login (used in copy throughout).
const DASHBOARD_DELIVERY_WINDOW = "24–48 hours";

// ============================================================================
// Animations (kept identical to ConsumerLanding to preserve premium feel)
// ============================================================================
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(20px) rotate(-5deg); }
`;

const scrollMarquee = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const fadeInStep = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 102, 255, 0.15); }
  50% { box-shadow: 0 0 40px rgba(0, 102, 255, 0.35); }
`;

const orbitSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const confettiBurst = keyframes`
  0% { transform: translate(0, 0) scale(0); opacity: 1; }
  20% { transform: translate(var(--cx), var(--cy)) scale(1.2); opacity: 1; }
  100% { transform: translate(var(--cx), calc(var(--cy) + 60px)) scale(0.3); opacity: 0; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.08); }
  100% { transform: scale(1); opacity: 1; }
`;

const goldShimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const goldPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 18px rgba(255, 215, 0, 0.45), 0 4px 14px rgba(184, 134, 11, 0.35);
  }
  50% {
    box-shadow: 0 0 55px rgba(255, 215, 0, 0.9), 0 8px 32px rgba(184, 134, 11, 0.65), 0 0 0 3px rgba(255, 215, 0, 0.25);
  }
`;

const silverOutlineGlow = keyframes`
  0% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); }
  25% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7), 0 8px 32px rgba(0, 0, 0, 0.2); }
  50% { box-shadow: 0 0 0 5px rgba(230, 230, 230, 1), 0 0 28px rgba(180, 180, 180, 0.85), 0 8px 32px rgba(0, 0, 0, 0.2); }
  75% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7), 0 8px 32px rgba(0, 0, 0, 0.2); }
  100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); }
`;

// ============================================================================
// Static content
// ============================================================================
const CONFETTI_PIECES = Array.from({ length: 24 }, (_, i) => {
  const angle = (i / 24) * 360;
  const distance = 60 + Math.random() * 70;
  const rad = (angle * Math.PI) / 180;
  return {
    cx: Math.cos(rad) * distance,
    cy: Math.sin(rad) * distance - 30,
    color: ['#0066FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'][i % 8],
    size: 4 + Math.random() * 5,
    delay: Math.random() * 0.3,
    shape: i % 3,
  };
});

const ANALYSIS_STEPS = [
  { label: "Identifying competitors in your city", sub: "Mapping every operator within your service radius", duration: 2800 },
  { label: "Pulling reviews & growth signals", sub: "Counts, momentum, and customer sentiment", duration: 2400 },
  { label: "Checking ad visibility", sub: "Who's running ads, what offers they use", duration: 2600 },
  { label: "Estimating website traffic", sub: "Domain authority, monthly visitors, paid + organic mix", duration: 2400 },
  { label: "Generating AI SWOT breakdown", sub: "Loading insights into your private dashboard", duration: 2400 },
];

// Niche suggestions for the dashboard request form are sourced from
// `data/businessTypes.js` and surfaced through the
// AutocompleteInput in both the hero form and lead-capture modal.

// Real customer case studies — copied verbatim from the localytics.co
// homepage (ConsumerLanding.js) so the social proof matches.
const CASE_STUDIES = [
  {
    name: "James Garrido",
    role: "Director of Ops",
    company: "Strategy Hospitality",
    date: "01-11-2024",
    quote:
      "We use Localytics in our monthly team meetings with staff... Show them what competitor bars are doing well vs what we are and how customer sentiment has changed.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bd9e0b1818ddd9c0ee.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/69137ab696d06a8d31791eef.png",
  },
  {
    name: "Brock Dugger",
    role: "Founder",
    company: "Busy Bee",
    date: "12-06-2024",
    quote:
      "We needed to know what market share we had in our niche, hyper locally, and how to take more. Y'all gave us that... Team's addicted to checking it.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bd3851a1931efe5a9d.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690926507b01ae40a353e46f.png",
  },
  {
    name: "Jack Carr",
    role: "CEO",
    company: "Rapid Response",
    date: "02-06-2025",
    quote:
      "Good internal tools exist. Tracking local competitors was not possible before we started using this thing. We literally find out what competitors suck at and then beat them, so cool.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/69138d114dedcf1b0ca9ed67.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690924a37b01ae20db539efd.png",
  },
  {
    name: "Nathan Stone",
    role: "CEO",
    company: "Supreme Plumbing",
    date: "10-06-2025",
    quote:
      "I was getting killed by the franchise down the street. Localytics showed me they were destroying me in 3 neighborhoods I thought I owned. Revenue up $47K this year just from that. This thing paid for itself in week one.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bdd825396eb95246b0.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690925cfe5ed230d32963158.png",
  },
];

// What Your Private Dashboard Includes — dark feature grid
const FEATURE_CARDS = [
  { icon: FaTrophy, title: "Competitor Rankings", description: "See the top businesses in your market ranked by reviews, growth, traffic, sentiment, and visibility." },
  { icon: FaChartLine, title: "Review Growth", description: "Understand which competitors are gaining momentum and which ones are slowing down." },
  { icon: FaStar, title: "Total Reviews", description: "See which businesses have the strongest trust moat before you enter the market." },
  { icon: FaCommentDots, title: "Customer Sentiment", description: "Find out what customers praise, complain about, and repeatedly mention." },
  { icon: FaBullhorn, title: "Paid Ad Visibility", description: "See which competitors are running ads and what offers they are using." },
  { icon: FaGlobe, title: "Website Traffic Estimates", description: "Understand which competitors appear to be getting attention online." },
  { icon: FaBrain, title: "AI SWOT Breakdown", description: "Get strengths, weaknesses, opportunities, and threats summarized for the market." },
  { icon: FaCity, title: "Market Concentration", description: "See whether the market is dominated by a few strong players or spread across many weaker ones." },
  { icon: FaTags, title: "Pricing & Offer Clues", description: "Identify pricing language, promotions, guarantees, and offers competitors are using." },
  { icon: FaExclamationTriangle, title: "Launch Risk Signals", description: "Understand what you'd be walking into before spending money on equipment, ads, rent, or hiring." },
];

// Map mockup callout chips
const MAP_CALLOUTS = [
  { label: "Review Leaders", color: "#10B981", position: { top: "8%", left: "6%" } },
  { label: "Fastest Growing", color: "#0066FF", position: { top: "8%", right: "6%" } },
  { label: "Heavy Advertisers", color: "#F59E0B", position: { bottom: "10%", left: "6%" } },
  { label: "Weak Competitors", color: "#EF4444", position: { bottom: "10%", right: "6%" } },
];

// "Starting Blind Is Expensive" bullets
const STARTING_BLIND = [
  { title: "You don't know who already owns the market", description: "Reviews, search rank, and visibility tell you who customers are choosing today — and how hard it'll be to take share." },
  { title: "You don't know how fast competitors are growing", description: "A market with one 5-star incumbent quietly compounding reviews is very different from a static one." },
  { title: "You don't know how expensive visibility may be", description: "If multiple competitors are bidding on Google ads, your customer-acquisition math changes overnight." },
  { title: "You don't know what customers complain about", description: "Recurring complaints are your easiest opening — but only if you know they exist before you launch." },
  { title: "You don't know whether you're entering a weak market or a knife fight", description: "Some cities are wide open. Others are saturated with operators who've been compounding trust for a decade." },
];

// "Why Generic Research Is Not Enough" comparison
const COMPARISON_FEATURES = [
  { feature: "Built for your exact city",            google: false, youtube: false, generic: false, localytics: true },
  { feature: "Built for your exact business idea",   google: false, youtube: false, generic: false, localytics: true },
  { feature: "Shows real local competitors",         google: "partial", youtube: false, generic: false, localytics: true },
  { feature: "Includes review growth",               google: false, youtube: false, generic: false, localytics: true },
  { feature: "Includes customer sentiment",          google: false, youtube: false, generic: "partial", localytics: true },
  { feature: "Includes ad visibility",               google: false, youtube: false, generic: false, localytics: true },
  { feature: "Includes traffic estimates",           google: false, youtube: false, generic: "partial", localytics: true },
  { feature: "Delivered as a private login dashboard", google: false, youtube: false, generic: false, localytics: true },
  { feature: "Helps you understand what you're walking into", google: false, youtube: false, generic: false, localytics: true },
];

// Pricing card content
const REPORT_OFFER = {
  name: "Private Market Intelligence Dashboard",
  price: REPORT_PRICE_USD,
  unit: "one-time",
  tagline: "One city. One business idea. Up to 20 competitors loaded into your private dashboard.",
  features: [
    "Private login access — explore anytime",
    "One city + one business idea",
    "Up to 20 competitors loaded in",
    "Review growth rankings",
    "Competitor ad visibility",
    "Website traffic estimates",
    "Customer sentiment breakdown",
    "AI SWOT analysis",
    `Login delivered in ${DASHBOARD_DELIVERY_WINDOW}`,
    "No subscription required",
  ],
  cta: "Get My Private Dashboard",
};

const FAQ_ITEMS = [
  {
    question: "What exactly do I get for $297?",
    answer:
      `A private market intelligence dashboard built for your city + business idea, with up to 20 competitors loaded in. You receive login access in ${DASHBOARD_DELIVERY_WINDOW} and can explore reviews, growth, ads, traffic, sentiment, and AI SWOT insights anytime — no subscription, no recurring charges.`
  },
  {
    question: "How do I access the dashboard?",
    answer:
      `Once your dashboard is built, we email you a private login link. Sign in from any browser to explore the data for your market. Access stays active so you can revisit it before, during, and after launch.`
  },
  {
    question: "What if my city or niche is unusual?",
    answer:
      "We've built dashboards for everything from junk removal in Nashville to med spas in Phoenix to dog grooming in Boise. If we don't have data for your market, we'll tell you within the hour and refund you."
  },
  {
    question: "How accurate is the data?",
    answer:
      "Reviews and ratings: 100% accurate (pulled live from Google). Traffic estimates: directional (±20%, based on SEO industry-standard tools). We label what's verified vs. estimated inside the dashboard."
  },
  {
    question: "What if I'm choosing between cities, not niches?",
    answer:
      "Add the $197 \u201csecond market\u201d option at checkout. Most buyers comparing markets do this so they can put two private dashboards side by side before committing."
  },
  {
    question: "Do you offer refunds?",
    answer:
      "Full refund if delivered late or factually wrong. No refunds for findings you don't like \u2014 that's not what you're paying for."
  },
  {
    question: "Is this a subscription?",
    answer:
      "No. One-time purchase, no recurring charges. You pay once, we deliver login access to your private dashboard, and that's it."
  },
];

const rateLimitService = getRateLimitService();

// ============================================================================
// Helper components
// ============================================================================
const FloatingElement = ({ children, animation, delay, size, position, opacity = 0.6 }) => (
  <Box
    position="absolute"
    {...position}
    animation={`${animation} ${6 + delay}s ease-in-out infinite`}
    animationDelay={`${delay}s`}
    opacity={opacity}
    pointerEvents="none"
    fontSize={size}
    zIndex={0}
    display={{ base: "none", md: "block" }}
  >
    {children}
  </Box>
);

// Minimal slim header — no SaaS pricing, no SMB onboarding links
const MinimalHeader = ({ onSeeExample }) => (
  <Box
    position="relative"
    bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
    zIndex={20}
  >
    <Container maxW="96vw" position="relative" zIndex={10}>
      <Flex justify="space-between" align="center" py={5} px={{ base: 2, md: 4 }}>
        <Image
          src="/output.png"
          alt="Localytics"
          h={{ base: "40px", md: "64px" }}
          w="auto"
          objectFit="contain"
          filter="brightness(0) invert(1)"
        />
        <Button
          onClick={onSeeExample}
          variant="ghost"
          color="white"
          fontWeight="medium"
          _hover={{ bg: "whiteAlpha.200", textDecoration: "none" }}
          size="md"
          fontSize={{ base: "sm", md: "md" }}
        >
          See Example Dashboard
        </Button>
      </Flex>
    </Container>
  </Box>
);

// Comparison cell renderer
const ComparisonCell = ({ value }) => {
  if (value === true) {
    return <Icon as={FaCheck} color="green.500" boxSize={{ base: 4, md: 5 }} />;
  }
  if (value === "partial") {
    return <Icon as={FaCheck} color="yellow.500" boxSize={{ base: 3.5, md: 4 }} opacity={0.7} />;
  }
  return <Icon as={FaTimes} color="red.400" boxSize={{ base: 3.5, md: 4 }} opacity={0.6} />;
};

// Mobile comparison card
const MobileComparisonCard = ({ row }) => (
  <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden" mb={3}>
    <Box bg="gray.50" px={4} py={2.5} borderBottomWidth="1px" borderColor="gray.200">
      <Text fontWeight="semibold" color="gray.800" fontSize="sm">{row.feature}</Text>
    </Box>
    <SimpleGrid columns={2} spacing={0}>
      <Box px={4} py={2.5} borderRightWidth="1px" borderBottomWidth="1px" borderColor="gray.200">
        <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>Google Search</Text>
        <ComparisonCell value={row.google} />
      </Box>
      <Box px={4} py={2.5} borderBottomWidth="1px" borderColor="gray.200">
        <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>YouTube Advice</Text>
        <ComparisonCell value={row.youtube} />
      </Box>
      <Box px={4} py={2.5} borderRightWidth="1px" borderColor="gray.200">
        <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>Generic Reports</Text>
        <ComparisonCell value={row.generic} />
      </Box>
      <Box px={4} py={2.5} bg="blue.50">
        <Text fontSize="xs" color="blue.600" fontWeight="bold" mb={1}>Localytics</Text>
        <ComparisonCell value={row.localytics} />
      </Box>
    </SimpleGrid>
  </Box>
);

// Scroll helpers (set inside the component, called from map iterators / nav)
let _scrollToExampleRef = null;
const scrollToExample = () => { if (_scrollToExampleRef) _scrollToExampleRef(); };

const MarketReportLanding = () => {
  const [city, setCity] = useState('');
  const [niche, setNiche] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showEligible, setShowEligible] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const analysisTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const eligibleTimerRef = useRef(null);

  const heroFormRef = useRef(null);
  const exampleRef = useRef(null);
  const offerRef = useRef(null);
  const cityInputRef = useRef(null);

  // Kept as a constant (vs state) so the hero-form glow effect can still
  // be triggered later by re-introducing a setter, without changing JSX.
  const highlightHeroForm = false;

  const cleanupAnalysisTimers = useCallback(() => {
    if (analysisTimerRef.current) {
      clearTimeout(analysisTimerRef.current);
      analysisTimerRef.current = null;
    }
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (eligibleTimerRef.current) {
      clearTimeout(eligibleTimerRef.current);
      eligibleTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanupAnalysisTimers;
  }, [cleanupAnalysisTimers]);

  const startAnalysis = useCallback(() => {
    setIsAnalyzing(true);
    setAnalysisStep(0);
    setAnalysisProgress(0);
    setShowModal(true);

    const totalDuration = ANALYSIS_STEPS.reduce((sum, s) => sum + s.duration, 0);
    const progressInterval = 50;
    let elapsed = 0;

    progressTimerRef.current = setInterval(() => {
      elapsed += progressInterval;
      const pct = Math.min((elapsed / totalDuration) * 100, 100);
      setAnalysisProgress(pct);

      let cumulative = 0;
      for (let i = 0; i < ANALYSIS_STEPS.length; i++) {
        cumulative += ANALYSIS_STEPS[i].duration;
        if (elapsed < cumulative) {
          setAnalysisStep(i);
          break;
        }
      }

      if (elapsed >= totalDuration) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
        setAnalysisProgress(100);
        setAnalysisStep(ANALYSIS_STEPS.length - 1);

        analysisTimerRef.current = setTimeout(() => {
          setIsAnalyzing(false);
          setShowEligible(true);
          eligibleTimerRef.current = setTimeout(() => {
            setShowEligible(false);
          }, 3000);
        }, 900);
      }
    }, progressInterval);
  }, []);

  const scrollToExampleImpl = useCallback(() => {
    if (exampleRef.current) {
      exampleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  _scrollToExampleRef = scrollToExampleImpl;

  // Primary CTA handler — opens the lead-capture modal so we always capture
  // the buyer's name/email/city/niche before redirecting them to Stripe.
  // (Match the homepage flow shown in the reference screenshot.)
  const handleGetReport = useCallback(() => {
    setIsAnalyzing(false);
    setShowEligible(false);
    setShowSuccess(false);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    cleanupAnalysisTimers();
    setShowModal(false);
    setIsAnalyzing(false);
    setShowEligible(false);
    setAnalysisStep(0);
    setAnalysisProgress(0);
  }, [cleanupAnalysisTimers]);

  const handleHeroSubmit = (e) => {
    e.preventDefault();
    if (city.trim() && niche.trim()) {
      startAnalysis();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rateLimitService.isRateLimited('marketReportSubmit')) {
      setError('Please wait a moment before trying again');
      return;
    }

    if (!email || !name || !city.trim() || !niche.trim()) return;

    if (phone.trim() && !smsConsent) {
      setError('Please check the box to agree to receive texts, or clear the phone field.');
      return;
    }

    setIsLoading(true);
    try {
      const compositeNiche = `[Private Dashboard $${REPORT_PRICE_USD}] ${niche.trim()} - ${city.trim()}`;
      // Capture the lead before kicking the buyer over to Stripe so we always
      // have their info even if they bounce on the checkout page.
      try {
        await submitLandingForm(email, compositeNiche, name, undefined, {
          phone: phone.trim() || undefined,
          smsConsent: phone.trim() ? smsConsent : undefined,
          smsConsentText: phone.trim() && smsConsent ? SMS_CONSENT_TEXT : undefined,
        });
      } catch (leadErr) {
        // Don't block checkout on a lead-capture hiccup — log and continue.
        // (rateLimitService / network errors shouldn't kill the purchase.)
      }
      const origin = window.location.origin;
      const successUrl = `${origin}/login?billing=success&email=${encodeURIComponent(email)}`;
      const cancelUrl = `${origin}/market-report?billing=canceled`;
      const { checkout_url } = await createLandingCheckoutSession(
        email,
        successUrl,
        cancelUrl,
        {
          name,
          niche: `${niche.trim()} in ${city.trim()}`,
        },
      );
      window.location.href = checkout_url;
    } catch (err) {
      setError('Checkout is unavailable right now. Please try again, or email help@localytics.co and we will set you up manually.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=1100, user-scalable=yes" />
        <title>Private Market Intelligence Dashboard | See the Entire Market First</title>
        <meta
          name="description"
          content="A private market intelligence dashboard built for your city + business idea. Up to 20 competitors loaded in — explore reviews, growth, ads, traffic, sentiment, and AI insights with login access in 24–48 hours."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content="Private Market Intelligence Dashboard" />
        <meta property="og:description" content="See the entire market before you start a local business. Private dashboard with up to 20 competitors loaded for your city + niche. Login access in 24–48 hours." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Box minH="100vh" bg="gray.50" overflowX="hidden">

        {/* ========================================================
            SECTION 1: HERO — premium gradient + dual-field form
            ======================================================== */}
        <Box
          position="relative"
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0} size="80px" position={{ top: "10%", left: "5%" }} opacity={0.3}>
            <Box bg="white" borderRadius="full" w="80px" h="80px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1} size="60px" position={{ top: "20%", right: "10%" }} opacity={0.25}>
            <Box bg="white" borderRadius="xl" w="60px" h="60px" transform="rotate(45deg)" />
          </FloatingElement>
          <FloatingElement animation={float} delay={2} size="100px" position={{ bottom: "20%", left: "8%" }} opacity={0.2}>
            <Box bg="white" borderRadius="full" w="100px" h="100px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={0.5} size="40px" position={{ bottom: "30%", right: "15%" }} opacity={0.3}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <MinimalHeader onSeeExample={scrollToExample} />

          <Container maxW="90vw" position="relative" zIndex={10}>
            <VStack
              spacing={{ base: 5, md: 6 }}
              textAlign="center"
              align="center"
              py={{ base: 16, md: 20, lg: 28 }}
              pb={{ base: 12, md: 16, lg: 20 }}
            >
              <AnimatedBox animation="fadeIn" delay={0.2}>
                <Text
                  textTransform="uppercase"
                  letterSpacing="0.25em"
                  fontSize={{ base: "xs", md: "sm" }}
                  color="whiteAlpha.800"
                  fontWeight="semibold"
                >
                  Private Market Intelligence Dashboard
                </Text>
              </AnimatedBox>

              <AnimatedBox animation="slideUp" delay={0.4}>
                <Heading
                  as="h1"
                  fontSize={{ base: "3xl", md: "5xl", lg: "6xl", xl: "7xl" }}
                  color="white"
                  lineHeight={1.1}
                  fontWeight="extrabold"
                  letterSpacing="tight"
                  maxW="1000px"
                >
                  Before You Start a Local Business,{" "}
                  <Text
                    as="span"
                    bgGradient="linear(to-r, yellow.200, orange.200)"
                    bgClip="text"
                  >
                    See the Entire Market First.
                  </Text>
                </Heading>
              </AnimatedBox>

              <AnimatedBox animation="fadeIn" delay={0.6}>
                <Text
                  fontSize={{ base: "md", md: "lg", lg: "xl" }}
                  color="whiteAlpha.900"
                  maxW="780px"
                  lineHeight={1.6}
                >
                  We build a private dashboard for your city + business idea with up to 20 competitors loaded in.
                  Get login access to explore reviews, growth, ads, traffic, sentiment, and AI insights before you launch.
                </Text>
              </AnimatedBox>

              {/* Dual-field hero form */}
              <Box ref={heroFormRef} w="full" maxW="760px">
                <AnimatedBox animation="slideUp" delay={0.8} w="full">
                  <form onSubmit={handleHeroSubmit}>
                    <Flex
                      bg="white"
                      borderRadius={{ base: "2xl", md: "full" }}
                      p={{ base: 2, md: 1.5 }}
                      align="center"
                      w="full"
                      direction={{ base: "column", md: "row" }}
                      gap={{ base: 2, md: 0 }}
                      transition="box-shadow 0.3s ease"
                      sx={highlightHeroForm ? {
                        animation: `${silverOutlineGlow} 1s ease-in-out 3`,
                        animationFillMode: 'forwards',
                      } : {
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <Flex align="center" w={{ base: "full", md: "auto" }} flex={{ base: "none", md: 1 }} pl={{ base: 2, md: 4 }}>
                        <Flex align="center" justify="center" pr={2} flexShrink={0}>
                          <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={{ base: 4, md: 5 }} />
                        </Flex>
                        <AutocompleteInput
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          getSuggestions={searchCities}
                          inputRef={cityInputRef}
                          inputProps={{
                            placeholder: "Nashville, TN",
                            "aria-label": "City",
                            size: "lg",
                            border: "none",
                            bg: "transparent",
                            _focus: { boxShadow: "none" },
                            _placeholder: { color: "gray.400" },
                            color: "gray.800",
                            fontSize: { base: "sm", md: "md" },
                            px: { base: 2, md: 3 },
                          }}
                        />
                      </Flex>

                      <Box display={{ base: "none", md: "block" }} w="1px" h="32px" bg="gray.200" flexShrink={0} />

                      <Flex align="center" w={{ base: "full", md: "auto" }} flex={{ base: "none", md: 1 }} pl={{ base: 2, md: 4 }}>
                        <Flex align="center" justify="center" pr={2} flexShrink={0}>
                          <Icon as={FaBriefcase} color="gray.400" boxSize={{ base: 4, md: 5 }} />
                        </Flex>
                        <AutocompleteInput
                          value={niche}
                          onChange={(e) => setNiche(e.target.value)}
                          getSuggestions={searchBusinessTypes}
                          inputProps={{
                            placeholder: "Junk removal, med spa, roofing, law firm...",
                            "aria-label": "Business idea",
                            size: "lg",
                            border: "none",
                            bg: "transparent",
                            _focus: { boxShadow: "none" },
                            _placeholder: { color: "gray.400" },
                            color: "gray.800",
                            fontSize: { base: "sm", md: "md" },
                            px: { base: 2, md: 3 },
                          }}
                        />
                      </Flex>

                      <Button
                        type="submit"
                        bg="#0066FF"
                        color="white"
                        borderRadius="full"
                        size="lg"
                        px={{ base: 5, md: 8 }}
                        fontWeight="bold"
                        leftIcon={<Icon as={FaSearch} boxSize={3.5} />}
                        flexShrink={0}
                        fontSize={{ base: "sm", md: "md" }}
                        w={{ base: "full", md: "auto" }}
                        _hover={{ bg: "#0052CC", transform: "translateY(-1px)", boxShadow: "lg" }}
                        transition="all 0.3s"
                      >
                        Analyze My Market
                      </Button>
                    </Flex>
                  </form>
                </AnimatedBox>

                <AnimatedBox animation="fadeIn" delay={1.0}>
                  <Text mt={3} fontSize="xs" color="whiteAlpha.700" textAlign="center">
                    One-time custom dashboard. <Text as="span" color="white" fontWeight="semibold">No subscription.</Text>{" "}
                    Login delivered in <Text as="span" color="white" fontWeight="semibold">{DASHBOARD_DELIVERY_WINDOW}</Text>.
                  </Text>
                </AnimatedBox>

                <AnimatedBox animation="fadeIn" delay={1.1}>
                  <HStack spacing={3} justify="center" mt={5} flexWrap="wrap">
                    <Button
                      onClick={handleGetReport}
                      bg="white"
                      color="#0066FF"
                      rightIcon={<FaArrowRight />}
                      size="md"
                      fontWeight="bold"
                      borderRadius="full"
                      px={6}
                      _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "gray.100" }}
                    >
                      Get My Private Dashboard
                    </Button>
                    <Button
                      onClick={scrollToExampleImpl}
                      variant="outline"
                      color="white"
                      borderColor="whiteAlpha.500"
                      borderWidth="2px"
                      size="md"
                      fontWeight="semibold"
                      borderRadius="full"
                      px={6}
                      _hover={{ bg: "whiteAlpha.100", borderColor: "whiteAlpha.700", transform: "translateY(-2px)" }}
                    >
                      See Example Dashboard
                    </Button>
                  </HStack>
                </AnimatedBox>
              </Box>
            </VStack>
          </Container>

          {/* Embedded competitor map preview with callout chips */}
          <Box pb={{ base: 0, md: 2 }}>
            <Container maxW="90vw" position="relative" zIndex={10}>
              <VStack spacing={4}>
                <AnimatedBox animation="scale" delay={0.5} w="full">
                  <Box
                    position="relative"
                    borderRadius={{ base: "xl", md: "2xl" }}
                    overflow="hidden"
                    boxShadow="0 12px 48px rgba(0, 0, 0, 0.3)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <LandingCompetitorMap />
                    {/* Premium callout chips overlaid on the map */}
                    {MAP_CALLOUTS.map((callout, idx) => (
                      <Box
                        key={callout.label}
                        position="absolute"
                        {...callout.position}
                        zIndex={5}
                        display={{ base: "none", md: "block" }}
                        animation={`${fadeInStep} 0.6s ease-out`}
                        sx={{ animationDelay: `${0.8 + idx * 0.15}s`, animationFillMode: 'both' }}
                      >
                        <HStack
                          spacing={2}
                          bg="white"
                          color="gray.800"
                          px={3.5}
                          py={2}
                          borderRadius="full"
                          boxShadow="0 8px 24px rgba(0,0,0,0.18)"
                          border="1px solid"
                          borderColor="gray.100"
                        >
                          <Box w="10px" h="10px" borderRadius="full" bg={callout.color} boxShadow={`0 0 0 4px ${callout.color}22`} />
                          <Text mb={0} fontSize="xs" fontWeight="bold" letterSpacing="wide">{callout.label}</Text>
                        </HStack>
                      </Box>
                    ))}
                  </Box>

                  {/* Mobile callout strip */}
                  <Flex
                    display={{ base: "flex", md: "none" }}
                    wrap="wrap"
                    gap={2}
                    justify="center"
                    mt={4}
                  >
                    {MAP_CALLOUTS.map((callout) => (
                      <HStack
                        key={callout.label}
                        spacing={1.5}
                        bg="white"
                        color="gray.800"
                        px={3}
                        py={1.5}
                        borderRadius="full"
                        boxShadow="0 4px 16px rgba(0,0,0,0.18)"
                      >
                        <Box w="8px" h="8px" borderRadius="full" bg={callout.color} />
                        <Text fontSize="xs" fontWeight="bold">{callout.label}</Text>
                      </HStack>
                    ))}
                  </Flex>
                </AnimatedBox>
              </VStack>
            </Container>
          </Box>

          <Box h={{ base: 8, md: 12 }} />
        </Box>

        {/* ========================================================
            SECTION 2: STATS BAR
            ======================================================== */}
        <Box
          py={{ base: 6, lg: 14 }}
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0} size="60px" position={{ top: "10%", left: "5%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="60px" h="60px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1} size="40px" position={{ bottom: "20%", right: "10%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <Container maxW="90vw" position="relative" zIndex={1}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 8 }}>
              {[
                { number: "Up to 20", label: "Competitors loaded in", icon: FaTrophy },
                { number: "24–48 hrs", label: "Login delivered", icon: FaRocket },
                { number: "Reviews · Ads · Traffic", label: "Market signals", icon: FaChartBar },
                { number: `$${REPORT_PRICE_USD}`, label: "One-time dashboard", icon: FaCheckCircle },
              ].map((stat, index) => (
                <AnimatedBox key={index} animation="scale" delay={0.1 * index}>
                  <VStack
                    spacing={{ base: 1.5, md: 3 }}
                    p={{ base: 3, md: 6 }}
                    bg="whiteAlpha.150"
                    borderRadius="2xl"
                    backdropFilter="blur(10px)"
                    h="full"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Flex w={{ base: "34px", md: "52px" }} h={{ base: "34px", md: "52px" }} justify="center" align="center" bg="whiteAlpha.200" borderRadius="full" flexShrink={0}>
                      <Icon as={stat.icon} boxSize={{ base: 4, md: 6 }} color="white" />
                    </Flex>
                    <Text fontSize={{ base: "md", md: "2xl" }} fontWeight="extrabold" color="white" lineHeight={1.1} textAlign="center">
                      {stat.number}
                    </Text>
                    <Text color="whiteAlpha.900" fontSize={{ base: "xs", md: "md" }} fontWeight="semibold" textAlign="center" lineHeight={1.3}>
                      {stat.label}
                    </Text>
                  </VStack>
                </AnimatedBox>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 3: FOUNDER STORY — "Built From a Real Local
            Business Decision"
            Moved up to position 3 (right after the stats bar) so cold
            traffic gets founder credibility before the feature dump.
            The "PICK your competitors" quote + 5-question framework
            + before/after card is the single biggest belief-builder
            on the page.
            ======================================================== */}
        <Box py={{ base: 14, lg: 20 }} bg="white" position="relative" overflow="hidden">
          <Container maxW="90vw">
            <VStack spacing={{ base: 8, md: 12 }} align="stretch">

              {/* Eyebrow + headline + subheadline */}
              <VStack spacing={3} textAlign="center" maxW="900px" mx="auto">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                    Founder Story
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800" lineHeight={1.2}>
                    Built From a Real{" "}
                    <Text as="span" color="blue.500">Local Business Decision</Text>
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.600" maxW="720px">
                    Localytics started because I needed a better way to understand local markets before choosing where to compete.
                  </Text>
                </AnimatedBox>
              </VStack>

              {/* Pull quote */}
              <AnimatedBox animation="slideUp" delay={0.5}>
                <Box
                  maxW="980px"
                  mx="auto"
                  bg="linear-gradient(135deg, #f0f6ff 0%, #e6efff 100%)"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="blue.100"
                  px={{ base: 6, md: 10 }}
                  py={{ base: 8, md: 10 }}
                  position="relative"
                  overflow="hidden"
                >
                  <Icon
                    as={FaQuoteLeft}
                    color="blue.100"
                    boxSize={{ base: 12, md: 16 }}
                    position="absolute"
                    top={{ base: 4, md: 6 }}
                    left={{ base: 4, md: 8 }}
                    opacity={0.9}
                  />
                  <VStack spacing={4} align="center" position="relative" zIndex={1}>
                    <Heading
                      as="blockquote"
                      size={{ base: "md", md: "lg" }}
                      color="gray.800"
                      lineHeight={1.35}
                      fontWeight="semibold"
                      textAlign="center"
                      maxW="780px"
                    >
                      &ldquo;The best thing about starting a business is you get to{" "}
                      <Text
                        as="span"
                        bgGradient="linear(to-r, #0066ff, #6d3fc0)"
                        bgClip="text"
                        fontWeight="extrabold"
                      >
                        PICK
                      </Text>
                      {" "}your competitors.&rdquo;
                    </Heading>
                    <HStack spacing={3}>
                      <Box w="32px" h="2px" bg="blue.400" borderRadius="full" />
                      <Text mb={0} fontSize="sm" color="gray.600" fontWeight="semibold">
                        Walter, Founder of Localytics
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </AnimatedBox>

              {/* OPERATOR CREDIBILITY — placed below the quote, above the
                  5-question framework. This is the single biggest trust lever
                  for a $297 buyer. */}
              <AnimatedBox animation="slideUp" delay={0.45}>
                <Box
                  maxW="980px"
                  mx="auto"
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="0 12px 40px rgba(0, 0, 0, 0.06)"
                  overflow="hidden"
                >
                  <Grid templateColumns={{ base: "1fr", md: "auto 1fr" }} gap={0}>
                    {/* Walter avatar / photo placeholder.
                        TODO: swap the gradient placeholder for an actual photo
                        of Walter once we have a hosted URL — change <Box>
                        below to <Image src="..." />. */}
                    <GridItem>
                      <Flex
                        h="full"
                        minH={{ base: "120px", md: "auto" }}
                        align="center"
                        justify="center"
                        bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
                        px={{ base: 6, md: 8 }}
                        py={{ base: 6, md: 0 }}
                      >
                        <Box
                          w={{ base: "88px", md: "112px" }}
                          h={{ base: "88px", md: "112px" }}
                          borderRadius="full"
                          bg="white"
                          color="#0066FF"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="extrabold"
                          fontSize={{ base: "3xl", md: "5xl" }}
                          boxShadow="0 8px 32px rgba(0,0,0,0.25)"
                          border="3px solid"
                          borderColor="whiteAlpha.300"
                        >
                          W
                        </Box>
                      </Flex>
                    </GridItem>

                    <GridItem>
                      <VStack align="start" spacing={4} px={{ base: 6, md: 8 }} py={{ base: 6, md: 8 }}>
                        <Heading size="md" color="gray.900" lineHeight={1.25}>
                          I'm not a market research analyst.{" "}
                          <Text as="span" color="blue.500">I'm an operator.</Text>
                        </Heading>

                        <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall">
                          In 2020, I started{" "}
                          <Text as="span" fontWeight="bold" color="gray.900">JunkDrop</Text>{" "}
                          in Nashville with no industry experience and a single truck. Five years later,
                          we're the #1 junk removal company in the city by review velocity &mdash;{" "}
                          <Text as="span" fontWeight="bold" color="gray.900">622+ reviews at 4.9 stars</Text>,
                          featured on TODAY, Nashville Scene, and News Channel 5.
                        </Text>

                        <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall">
                          I didn't get there by guessing. I got there by knowing exactly who I was competing
                          against, where they were weak, and where the market had room.{" "}
                          <Text as="span" fontWeight="bold" color="gray.900">
                            Localytics is the tool I built to do that &mdash; first for myself, now for you.
                          </Text>
                        </Text>

                        {/* Trust strip: stats + as-seen-on */}
                        <Divider borderColor="gray.200" />
                        <Flex
                          w="full"
                          gap={{ base: 3, md: 5 }}
                          wrap="wrap"
                          align="center"
                        >
                          {/* Hard stats */}
                          <HStack
                            spacing={2}
                            bg="green.50"
                            border="1px solid"
                            borderColor="green.200"
                            px={3}
                            py={1.5}
                            borderRadius="full"
                          >
                            <Icon as={FaStar} color="yellow.400" boxSize={3} />
                            <Text mb={0} fontSize="xs" fontWeight="bold" color="gray.800">4.9 / 5</Text>
                            <Text mb={0} fontSize="xs" color="gray.500">&middot;</Text>
                            <Text mb={0} fontSize="xs" fontWeight="semibold" color="gray.700">622+ reviews</Text>
                          </HStack>
                          <HStack
                            spacing={2}
                            bg="blue.50"
                            border="1px solid"
                            borderColor="blue.200"
                            px={3}
                            py={1.5}
                            borderRadius="full"
                          >
                            <Icon as={FaTrophy} color="blue.500" boxSize={3} />
                            <Text mb={0} fontSize="xs" fontWeight="bold" color="gray.800">#1 in Nashville</Text>
                            <Text mb={0} fontSize="xs" color="gray.500">&middot;</Text>
                            <Text mb={0} fontSize="xs" fontWeight="semibold" color="gray.700">review velocity</Text>
                          </HStack>

                          {/* As seen on */}
                          <HStack spacing={2} flexWrap="wrap" ml={{ base: 0, md: "auto" }}>
                            <Text
                              fontSize="xs"
                              color="gray.400"
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="0.1em"
                            >
                              As seen on
                            </Text>
                            {["TODAY", "Nashville Scene", "News Channel 5"].map((outlet) => (
                              <Text
                                key={outlet}
                                fontSize="xs"
                                fontWeight="bold"
                                color="gray.600"
                                px={2.5}
                                py={1}
                                bg="gray.50"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                letterSpacing="wide"
                              >
                                {outlet}
                              </Text>
                            ))}
                          </HStack>
                        </Flex>
                      </VStack>
                    </GridItem>
                  </Grid>
                </Box>
              </AnimatedBox>

              {/* 2-column layout — founder story + before/after card */}
              <Grid
                templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                gap={{ base: 8, lg: 10 }}
                alignItems="start"
              >
                {/* LEFT — founder story copy */}
                <GridItem>
                  <AnimatedBox animation="slideUp" delay={0.5}>
                    <VStack align="start" spacing={5}>
                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall">
                        I didn't want to start a business based on gut feeling, YouTube advice, or vague market
                        research. I wanted to know what was actually happening in the city I was considering.
                      </Text>

                      {/* Inline question list — premium pill style */}
                      <VStack align="stretch" spacing={2.5} w="full">
                        {[
                          "Who already had the most reviews?",
                          "Who was growing the fastest?",
                          "Which competitors were running ads?",
                          "What were customers complaining about?",
                          "Was the market dominated by a few strong players, or were there weaknesses I could exploit?",
                        ].map((q, i) => (
                          <HStack
                            key={i}
                            spacing={3}
                            align="flex-start"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.100"
                            borderRadius="xl"
                            px={4}
                            py={3}
                            transition="all 0.2s"
                            _hover={{ borderColor: "blue.200", bg: "blue.50" }}
                          >
                            <Flex
                              w="22px"
                              h="22px"
                              borderRadius="full"
                              bg="blue.100"
                              align="center"
                              justify="center"
                              flexShrink={0}
                              mt={0.5}
                            >
                              <Text fontSize="xs" fontWeight="bold" color="blue.600">{i + 1}</Text>
                            </Flex>
                            <Text fontSize={{ base: "sm", md: "md" }} color="gray.700" fontWeight="medium" lineHeight={1.5}>
                              {q}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>

                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall" fontWeight="semibold">
                        That's the insight Localytics was built to provide.
                      </Text>

                      <Divider borderColor="gray.200" />

                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall">
                        When you start a local business, you don't just pick an industry.{" "}
                        <Text as="span" fontWeight="bold" color="gray.900">
                          You pick a battlefield.
                        </Text>{" "}
                        Most people walk in blind. The ones who win walk in knowing exactly who they're fighting
                        and where the gaps are.
                      </Text>

                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="tall">
                        This dashboard gives you the same kind of competitor visibility I wanted before making
                        those decisions.
                      </Text>
                    </VStack>
                  </AnimatedBox>
                </GridItem>

                {/* RIGHT — premium before/after card */}
                <GridItem>
                  <AnimatedBox animation="slideUp" delay={0.6}>
                    <Box
                      bg="white"
                      borderRadius="2xl"
                      border="1px solid"
                      borderColor="gray.200"
                      boxShadow="0 12px 40px rgba(0, 0, 0, 0.06)"
                      overflow="hidden"
                      position="sticky"
                      top={{ base: "auto", lg: "100px" }}
                      transition="all 0.3s"
                      _hover={{ transform: "translateY(-4px)", boxShadow: "0 20px 60px rgba(0, 102, 255, 0.12)" }}
                    >
                      {/* Card header */}
                      <Box
                        bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
                        px={{ base: 5, md: 7 }}
                        py={{ base: 5, md: 6 }}
                      >
                        <VStack align="start" spacing={1.5}>
                          <Text
                            textTransform="uppercase"
                            letterSpacing="0.2em"
                            fontSize="xs"
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                          >
                            From Guessing → To Knowing
                          </Text>
                          <Heading size="md" color="white" lineHeight={1.25}>
                            What Localytics Helps You See
                          </Heading>
                        </VStack>
                      </Box>

                      {/* Card body */}
                      <VStack align="stretch" spacing={0} p={{ base: 5, md: 7 }}>
                        {/* Before */}
                        <VStack align="stretch" spacing={3}>
                          <HStack spacing={2}>
                            <Badge
                              colorScheme="red"
                              variant="subtle"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                              fontSize="xs"
                              textTransform="uppercase"
                              letterSpacing="wider"
                            >
                              Before
                            </Badge>
                            <Text mb={0} fontSize="sm" color="gray.500" fontStyle="italic">
                              Without competitive context
                            </Text>
                          </HStack>
                          <VStack align="stretch" spacing={2}>
                            {[
                              "Guessing which market is best",
                              "Relying on generic advice",
                              "Underestimating competitors",
                              "Missing obvious market weaknesses",
                            ].map((item, i) => (
                              <HStack key={i} spacing={3} align="flex-start">
                                <Flex
                                  w="20px"
                                  h="20px"
                                  borderRadius="full"
                                  bg="red.50"
                                  align="center"
                                  justify="center"
                                  flexShrink={0}
                                  mt={0.5}
                                >
                                  <Icon as={FaTimes} color="red.400" boxSize={2.5} />
                                </Flex>
                                <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" lineHeight={1.5}>
                                  {item}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        </VStack>

                        {/* Divider arrow */}
                        <Flex justify="center" py={5}>
                          <Flex
                            w={9}
                            h={9}
                            borderRadius="full"
                            bg="blue.50"
                            align="center"
                            justify="center"
                            border="1px solid"
                            borderColor="blue.100"
                          >
                            <Icon as={FaArrowDown} color="blue.500" boxSize={3.5} />
                          </Flex>
                        </Flex>

                        {/* After */}
                        <VStack align="stretch" spacing={3}>
                          <HStack spacing={2}>
                            <Badge
                              colorScheme="green"
                              variant="subtle"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                              fontSize="xs"
                              textTransform="uppercase"
                              letterSpacing="wider"
                            >
                              After
                            </Badge>
                            <Text mb={0} fontSize="sm" color="gray.500" fontStyle="italic">
                              With your private dashboard
                            </Text>
                          </HStack>
                          <VStack align="stretch" spacing={2}>
                            {[
                              "See up to 20 competitors",
                              "Understand who is winning",
                              "Spot growth and advertising patterns",
                              "Enter the market with context",
                            ].map((item, i) => (
                              <HStack key={i} spacing={3} align="flex-start">
                                <Flex
                                  w="20px"
                                  h="20px"
                                  borderRadius="full"
                                  bg="green.50"
                                  align="center"
                                  justify="center"
                                  flexShrink={0}
                                  mt={0.5}
                                >
                                  <Icon as={FaCheck} color="green.500" boxSize={2.5} />
                                </Flex>
                                <Text fontSize={{ base: "sm", md: "md" }} color="gray.700" fontWeight="medium" lineHeight={1.5}>
                                  {item}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        </VStack>

                        {/* Highlighted callout */}
                        <Box
                          mt={6}
                          bg="linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
                          border="1px solid"
                          borderColor="yellow.300"
                          borderRadius="xl"
                          px={4}
                          py={3.5}
                        >
                          <HStack spacing={3} align="center">
                            <Flex
                              w={8}
                              h={8}
                              borderRadius="full"
                              bg="white"
                              align="center"
                              justify="center"
                              flexShrink={0}
                              boxShadow="0 2px 8px rgba(180, 130, 0, 0.25)"
                            >
                              <Icon as={FaTrophy} color="yellow.600" boxSize={3.5} />
                            </Flex>
                            <Text mb={0} fontSize={{ base: "sm", md: "md" }} color="gray.900" fontWeight="bold" lineHeight={1.3}>
                              Before you start, know who you're up against.
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    </Box>
                  </AnimatedBox>
                </GridItem>
              </Grid>

              {/* Section CTA */}
              <AnimatedBox animation="fadeIn" delay={0.7}>
                <VStack spacing={3}>
                  <Button
                    size="lg"
                    bg="#0066FF"
                    color="white"
                    rightIcon={<FaArrowRight />}
                    onClick={handleGetReport}
                    px={10}
                    fontWeight="bold"
                    borderRadius="full"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                  >
                    Build My Private Dashboard
                  </Button>
                  <Text fontSize="xs" color="gray.500">
                    ${REPORT_PRICE_USD} one-time &middot; login delivered in {DASHBOARD_DELIVERY_WINDOW}
                  </Text>
                </VStack>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 5: DARK FEATURE GRID — "What Your Private Dashboard Includes"
            ======================================================== */}
        <Box
          py={{ base: 14, lg: 20 }}
          bg="linear-gradient(135deg, #0a0e1a 0%, #1a1a2e 50%, #0f1d3a 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0.5} size="60px" position={{ top: "8%", right: "6%" }} opacity={0.08}>
            <Box bg="white" borderRadius="full" w="60px" h="60px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1.2} size="40px" position={{ bottom: "12%", left: "5%" }} opacity={0.08}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <Container maxW="90vw" position="relative" zIndex={1}>
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge bg="whiteAlpha.200" color="white" px={3} py={1} borderRadius="full" fontSize="sm" border="1px solid" borderColor="whiteAlpha.300">
                    What's Inside
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="white" lineHeight={1.2}>
                    What Your Private Dashboard{" "}
                    <Text as="span" bgGradient="linear(to-r, #00d2ff, #3a7bd5)" bgClip="text">
                      Includes
                    </Text>
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="whiteAlpha.800" maxW="640px">
                    Ten layers of competitive intelligence — loaded into your private login and built specifically for your city + business idea.
                  </Text>
                </AnimatedBox>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={5} w="full">
                {FEATURE_CARDS.map((card, index) => (
                  <AnimatedBox key={index} animation="slideUp" delay={0.1 + index * 0.04}>
                    <Box
                      p={6}
                      bg="whiteAlpha.100"
                      borderRadius="2xl"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      backdropFilter="blur(10px)"
                      h="full"
                      transition="all 0.3s"
                      _hover={{
                        transform: "translateY(-4px)",
                        bg: "whiteAlpha.150",
                        borderColor: "whiteAlpha.400",
                        boxShadow: "0 12px 32px rgba(0, 102, 255, 0.18)"
                      }}
                    >
                      <VStack align="start" spacing={3}>
                        <Flex
                          w={11}
                          h={11}
                          borderRadius="xl"
                          bg="linear-gradient(135deg, #0066ff 0%, #00d2ff 100%)"
                          align="center"
                          justify="center"
                          flexShrink={0}
                          boxShadow="0 8px 24px rgba(0, 102, 255, 0.3)"
                        >
                          <Icon as={card.icon} color="white" boxSize={5} />
                        </Flex>
                        <Heading size="sm" color="white" lineHeight={1.3}>{card.title}</Heading>
                        <Text fontSize="sm" color="whiteAlpha.700" lineHeight="tall">{card.description}</Text>
                      </VStack>
                    </Box>
                  </AnimatedBox>
                ))}
              </SimpleGrid>

              <AnimatedBox animation="fadeIn" delay={0.6}>
                <Button
                  size="lg"
                  bg="white"
                  color="#0066FF"
                  rightIcon={<FaArrowRight />}
                  onClick={handleGetReport}
                  px={10}
                  fontWeight="bold"
                  borderRadius="full"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 12px 40px rgba(255,255,255,0.2)", bg: "gray.100" }}
                >
                  Get My Private Dashboard
                </Button>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 6: INTERACTIVE DASHBOARD SHOWCASE
            ======================================================== */}
        <LandingDashboardShowcase
          heading="Every important question about your market,"
          headingHighlight="answered in one private dashboard"
          subheading="Click any question below to explore a live, interactive dashboard built from real competitive data. Yours will be customized to your city + business idea."
        />

        {/* ========================================================
            SECTION 7: HOW YOUR PRIVATE DASHBOARD WORKS — 3-step
            "what happens after purchase" flow that buyers asked for.
            Kept simple and concrete so cold buyers can grok the offer
            in 5 seconds: pay → choose city/niche → log in.
            ======================================================== */}
        <Box py={{ base: 14, lg: 20 }} bg="white">
          <Container maxW="90vw">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="820px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                    What Happens After You Purchase
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800" lineHeight={1.2}>
                    How Your <Text as="span" color="blue.500">Private Dashboard</Text> Works
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize={{ base: "md", md: "lg" }} color="gray.500" maxW="720px">
                    Pay once, tell us your city + business idea, get login access in {DASHBOARD_DELIVERY_WINDOW}.
                    No subscription, no software to install.
                  </Text>
                </AnimatedBox>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} w="full">
                {[
                  {
                    step: "01",
                    title: "You choose your city + business idea",
                    description: "Example: \u201cNashville junk removal\u201d or \u201cDallas med spa.\u201d One city, one business idea."
                  },
                  {
                    step: "02",
                    title: "We build your private competitor dashboard",
                    description: "We load up to 20 competitors and analyze reviews, growth, ads, traffic, sentiment, and AI SWOT."
                  },
                  {
                    step: "03",
                    title: `You receive login access in ${DASHBOARD_DELIVERY_WINDOW}`,
                    description: "Sign in from any browser to explore your market before you launch. No subscription, access stays active."
                  },
                ].map((item, idx) => (
                  <AnimatedBox key={idx} animation="slideUp" delay={0.2 + idx * 0.08}>
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="2xl"
                      border="1px solid"
                      borderColor="gray.200"
                      h="full"
                      position="relative"
                      transition="all 0.3s"
                      _hover={{ transform: "translateY(-4px)", boxShadow: "lg", borderColor: "blue.300" }}
                    >
                      <VStack align="start" spacing={3}>
                        <Flex
                          w={12}
                          h={12}
                          borderRadius="xl"
                          bg="blue.50"
                          align="center"
                          justify="center"
                          flexShrink={0}
                        >
                          <Text fontWeight="extrabold" color="blue.500" fontSize="lg">{item.step}</Text>
                        </Flex>
                        <Heading size="sm" color="gray.800" lineHeight={1.3}>{item.title}</Heading>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.description}</Text>
                      </VStack>
                    </Box>
                  </AnimatedBox>
                ))}
              </SimpleGrid>

              <AnimatedBox animation="fadeIn" delay={0.6}>
                <Button
                  size="lg"
                  bg="#0066FF"
                  color="white"
                  rightIcon={<FaArrowRight />}
                  onClick={handleGetReport}
                  px={10}
                  fontWeight="bold"
                  borderRadius="full"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                >
                  Get My Private Dashboard
                </Button>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 9: STARTING BLIND IS EXPENSIVE
            (Replaces the toggle comparison block; same neutral cream surface.)
            ======================================================== */}
        <Box py={{ base: 14, lg: 20 }} bg="#FDF8F3">
          <Container maxW="900px">
            <VStack spacing={{ base: 8, md: 10 }}>
              <VStack spacing={4} textAlign="center" maxW="800px">
                <AnimatedBox animation="slideUp" delay={0.2}>
                  <Heading
                    size={{ base: "xl", md: "2xl" }}
                    color="gray.800"
                    lineHeight={1.15}
                    fontWeight="bold"
                    px={{ base: 2, md: 0 }}
                  >
                    Starting Blind Is Expensive
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.3}>
                  <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" lineHeight="tall">
                    Most people pick a business idea based on YouTube videos, gut feeling, or what seems popular.
                    But local markets are not equal. One city may be wide open. Another may be dominated by competitors
                    with hundreds of reviews, aggressive ads, and years of trust.
                  </Text>
                </AnimatedBox>
              </VStack>

              <Box
                w="full"
                bg="white"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                boxShadow="lg"
              >
                <VStack spacing={0} align="stretch">
                  {STARTING_BLIND.map((item, index) => (
                    <HStack
                      key={index}
                      spacing={{ base: 4, md: 5 }}
                      px={{ base: 5, md: 8 }}
                      py={{ base: 5, md: 6 }}
                      borderBottomWidth={index < STARTING_BLIND.length - 1 ? "1px" : "0"}
                      borderColor="gray.100"
                      align="flex-start"
                      transition="all 0.3s ease"
                      sx={{
                        animation: `${fadeInStep} 0.4s ease forwards`,
                        animationDelay: `${index * 0.06}s`,
                        opacity: 0,
                      }}
                    >
                      <Flex
                        w={{ base: "36px", md: "44px" }}
                        h={{ base: "36px", md: "44px" }}
                        borderRadius="xl"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        bg="red.50"
                        mt={0.5}
                      >
                        <Icon as={FaTimes} boxSize={{ base: 3.5, md: 4 }} color="red.400" />
                      </Flex>
                      <VStack align="start" spacing={1} flex="1">
                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }} color="gray.800">
                          {item.title}
                        </Text>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" lineHeight="tall">
                          {item.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <AnimatedBox animation="fadeIn" delay={0.4}>
                <Button
                  size="lg"
                  bg="#0066FF"
                  color="white"
                  rightIcon={<FaArrowRight />}
                  onClick={handleGetReport}
                  px={10}
                  fontWeight="bold"
                  borderRadius="full"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                >
                  Get My Private Dashboard
                </Button>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 9.6: WHAT YOU GET — annotated dashboard preview.
            Shows the deliverable so the buyer knows what they're paying for.
            This is also the "Example Dashboard" anchor target for the header
            and hero CTA, since we removed the standalone demo video section
            (the dashboard previews here are stronger proof than a generic
            walkthrough video).
            TODO: swap REPORT_PREVIEW_IMAGE for the actual hosted Junkluggers
            report screenshot once we have a URL.
            ======================================================== */}
        <Box ref={exampleRef} py={{ base: 14, lg: 20 }} bg="gray.50" id="demo-video">
          <Container maxW="90vw">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                    The Deliverable
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800" lineHeight={1.2}>
                    What you get:{" "}
                    <Text as="span" color="blue.500">login access to your private market intelligence dashboard</Text>
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.600">
                    Not a static PDF. Not a generic template. A live, private dashboard built from real data on
                    your exact city + niche — that you can sign in to and explore anytime.
                  </Text>
                </AnimatedBox>
              </VStack>

              <AnimatedBox animation="slideUp" delay={0.5} w="full">
                <Grid templateColumns={{ base: "1fr", lg: "1.6fr 1fr" }} gap={{ base: 8, lg: 10 }} alignItems="start">
                  {/* LEFT — annotated screenshot */}
                  <GridItem>
                    <Box
                      position="relative"
                      borderRadius="2xl"
                      overflow="hidden"
                      boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
                      border="1px solid"
                      borderColor="gray.200"
                      bg="white"
                    >
                      <Image
                        src="https://assets.cdn.filesafe.space/QGPyKOzkQsEY8D8pCfT8/media/695d8a57a88e87258ec9f1d6.png"
                        alt="Sample Localytics private market dashboard — Nashville moving company"
                        w="full"
                        objectFit="cover"
                        objectPosition="top"
                        display="block"
                      />

                      {/* Numbered annotation pins overlaid on the screenshot.
                          Desktop only — mobile gets the legend list below. */}
                      {[
                        { num: 1, color: "#0066FF", position: { top: "8%", left: "6%" } },
                        { num: 2, color: "#10B981", position: { top: "26%", right: "7%" } },
                        { num: 3, color: "#F59E0B", position: { top: "52%", left: "10%" } },
                        { num: 4, color: "#EC4899", position: { top: "62%", right: "10%" } },
                        { num: 5, color: "#6D3FC0", position: { bottom: "8%", left: "40%" } },
                      ].map((pin) => (
                        <Flex
                          key={pin.num}
                          position="absolute"
                          {...pin.position}
                          display={{ base: "none", md: "flex" }}
                          w="34px"
                          h="34px"
                          borderRadius="full"
                          bg={pin.color}
                          color="white"
                          align="center"
                          justify="center"
                          fontWeight="extrabold"
                          fontSize="sm"
                          boxShadow={`0 8px 24px ${pin.color}55, 0 0 0 4px white`}
                          zIndex={2}
                        >
                          {pin.num}
                        </Flex>
                      ))}
                    </Box>
                    <Text fontSize="xs" color="gray.500" textAlign="center" mt={3} fontStyle="italic">
                      Sample dashboard generated for a Nashville moving company. Yours will look like this, customized
                      to your city and niche — accessible via your private login.
                    </Text>
                  </GridItem>

                  {/* RIGHT — annotation legend */}
                  <GridItem>
                    <VStack align="stretch" spacing={3}>
                      {[
                        {
                          num: 1,
                          color: "#0066FF",
                          title: "Composite score",
                          desc: "How the market leaders rank across reviews, growth, traffic, and visibility.",
                        },
                        {
                          num: 2,
                          color: "#10B981",
                          title: "Review velocity trends",
                          desc: "Who's growing month-over-month, who's stagnant, who's about to break out.",
                        },
                        {
                          num: 3,
                          color: "#F59E0B",
                          title: "Customer sentiment themes",
                          desc: "What buyers actually praise and complain about — your easiest market openings.",
                        },
                        {
                          num: 4,
                          color: "#EC4899",
                          title: "Active ad creatives",
                          desc: "See exactly how competitors are positioning, what offers they're testing.",
                        },
                        {
                          num: 5,
                          color: "#6D3FC0",
                          title: "AI SWOT analysis",
                          desc: "Strengths, weaknesses, opportunities, threats — built from real data, not generic templates.",
                        },
                      ].map((item) => (
                        <HStack
                          key={item.num}
                          align="flex-start"
                          spacing={3}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="xl"
                          px={4}
                          py={3.5}
                          transition="all 0.2s"
                          _hover={{ borderColor: "blue.300", boxShadow: "md", transform: "translateY(-2px)" }}
                        >
                          <Flex
                            w="32px"
                            h="32px"
                            borderRadius="full"
                            bg={item.color}
                            color="white"
                            align="center"
                            justify="center"
                            fontWeight="extrabold"
                            fontSize="sm"
                            flexShrink={0}
                            boxShadow={`0 4px 12px ${item.color}40`}
                          >
                            {item.num}
                          </Flex>
                          <VStack align="start" spacing={0.5}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.900" lineHeight={1.3}>
                              {item.title}
                            </Text>
                            <Text fontSize="xs" color="gray.600" lineHeight={1.5}>
                              {item.desc}
                            </Text>
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  </GridItem>
                </Grid>
              </AnimatedBox>

              <AnimatedBox animation="fadeIn" delay={0.7}>
                <Button
                  size="lg"
                  bg="#0066FF"
                  color="white"
                  rightIcon={<FaArrowRight />}
                  onClick={handleGetReport}
                  px={10}
                  fontWeight="bold"
                  borderRadius="full"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                >
                  Get My Private Dashboard &mdash; ${REPORT_PRICE_USD}
                </Button>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 9.7: TWO EXAMPLE CASE STUDIES
            (JunkDrop — Walter's own — + Junkluggers, side-by-side.
            Real data layer that proves analytical depth without
            reading as a vanity case study.)
            ======================================================== */}
        <Box py={{ base: 14, lg: 20 }} bg="white">
          <Container maxW="90vw">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="sm">
                    Two Real Examples
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800" lineHeight={1.2}>
                    What the data <Text as="span" color="blue.500">actually shows</Text>
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.600">
                    Two real Localytics analyses &mdash; one of my own business, one of a competitor.
                  </Text>
                </AnimatedBox>
              </VStack>

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} w="full">
                {/* JunkDrop card */}
                <AnimatedBox animation="slideUp" delay={0.4}>
                  <Box
                    bg="white"
                    borderRadius="2xl"
                    border="2px solid"
                    borderColor="blue.100"
                    overflow="hidden"
                    boxShadow="0 12px 40px rgba(0, 0, 0, 0.06)"
                    h="full"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "0 20px 60px rgba(0, 102, 255, 0.15)" }}
                  >
                    <Box bg="linear-gradient(135deg, #0066ff 0%, #003d99 100%)" px={{ base: 5, md: 6 }} py={4}>
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xs" color="whiteAlpha.800" fontWeight="bold" textTransform="uppercase" letterSpacing="0.15em">
                            Example 1 &middot; My own business
                          </Text>
                          <Heading size="md" color="white">JunkDrop &middot; Nashville</Heading>
                        </VStack>
                        <Badge bg="green.400" color="white" px={2.5} py={1} borderRadius="full" fontSize="xs" fontWeight="bold">
                          Operator-led
                        </Badge>
                      </HStack>
                    </Box>
                    <VStack align="stretch" spacing={4} p={{ base: 5, md: 6 }}>
                      <SimpleGrid columns={2} spacing={3}>
                        {[
                          { label: "Review velocity rank", value: "#1" },
                          { label: "Total reviews", value: "622" },
                          { label: "Average rating", value: "4.9 ★" },
                          { label: "90-day reviews", value: "+57" },
                        ].map((stat) => (
                          <Box
                            key={stat.label}
                            bg="gray.50"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="gray.100"
                            px={3}
                            py={3}
                          >
                            <Text fontSize="2xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.1em">
                              {stat.label}
                            </Text>
                            <Text fontSize="xl" fontWeight="extrabold" color="gray.900" lineHeight={1.2} mt={1}>
                              {stat.value}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>

                      <VStack align="stretch" spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="0.1em">
                          Beat in growth rate
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {["Junk King", "Stand Up Guys", "LoadUp"].map((name) => (
                            <Text
                              key={name}
                              fontSize="xs"
                              fontWeight="semibold"
                              color="gray.700"
                              px={2.5}
                              py={1}
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                            >
                              {name}
                            </Text>
                          ))}
                        </Flex>
                      </VStack>

                      <Box
                        bg="blue.50"
                        border="1px solid"
                        borderColor="blue.100"
                        borderRadius="xl"
                        px={4}
                        py={3.5}
                      >
                        <HStack align="flex-start" spacing={3}>
                          <Icon as={FaQuoteLeft} color="blue.300" boxSize={4} mt={0.5} />
                          <Text fontSize="sm" color="gray.700" lineHeight="tall" fontStyle="italic">
                            When I started, Junk King had a 5-year head start. Localytics showed me their weakness:
                            slow review velocity. I attacked that lane and won it in 18 months.
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={2} textAlign="right" fontWeight="semibold">
                          &mdash; Walter, JunkDrop
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                </AnimatedBox>

                {/* Junkluggers card */}
                <AnimatedBox animation="slideUp" delay={0.5}>
                  <Box
                    bg="white"
                    borderRadius="2xl"
                    border="2px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                    boxShadow="0 12px 40px rgba(0, 0, 0, 0.06)"
                    h="full"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)" }}
                  >
                    <Box bg="linear-gradient(135deg, #6d3fc0 0%, #c02060 100%)" px={{ base: 5, md: 6 }} py={4}>
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xs" color="whiteAlpha.800" fontWeight="bold" textTransform="uppercase" letterSpacing="0.15em">
                            Example 2 &middot; Competitor analysis
                          </Text>
                          <Heading size="md" color="white">Junkluggers</Heading>
                        </VStack>
                        <Badge bg="whiteAlpha.300" color="white" px={2.5} py={1} borderRadius="full" fontSize="xs" fontWeight="bold">
                          Anonymized
                        </Badge>
                      </HStack>
                    </Box>
                    <VStack align="stretch" spacing={4} p={{ base: 5, md: 6 }}>
                      <SimpleGrid columns={2} spacing={3}>
                        {[
                          { label: "Growth rank", value: "#5" },
                          { label: "Traffic rank", value: "#3" },
                          { label: "Sentiment", value: "98.7%" },
                          { label: "Sentiment trend", value: "↓ Declining" },
                        ].map((stat) => (
                          <Box
                            key={stat.label}
                            bg="gray.50"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="gray.100"
                            px={3}
                            py={3}
                          >
                            <Text fontSize="2xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.1em">
                              {stat.label}
                            </Text>
                            <Text fontSize="xl" fontWeight="extrabold" color="gray.900" lineHeight={1.2} mt={1}>
                              {stat.value}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>

                      <VStack align="stretch" spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="0.1em">
                          Identified vulnerability
                        </Text>
                        <HStack
                          align="flex-start"
                          spacing={3}
                          bg="orange.50"
                          border="1px solid"
                          borderColor="orange.100"
                          borderRadius="xl"
                          px={4}
                          py={3}
                        >
                          <Flex
                            w={6}
                            h={6}
                            borderRadius="full"
                            bg="orange.100"
                            align="center"
                            justify="center"
                            flexShrink={0}
                          >
                            <Icon as={FaExclamationTriangle} color="orange.500" boxSize={3} />
                          </Flex>
                          <Text fontSize="sm" color="gray.700" lineHeight="tall">
                            Recurring{" "}
                            <Text as="span" fontWeight="bold" color="gray.900">scheduling complaints</Text>{" "}
                            in reviews &mdash; an easy attack lane for any new entrant.
                          </Text>
                        </HStack>
                      </VStack>

                      <Box
                        bg="purple.50"
                        border="1px solid"
                        borderColor="purple.100"
                        borderRadius="xl"
                        px={4}
                        py={3.5}
                      >
                        <Text fontSize="sm" color="gray.700" lineHeight="tall">
                          A competitor with strong sentiment but slipping operations is a competitor losing share.
                          Your private dashboard tells you exactly where they're slipping &mdash; before you launch.
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                </AnimatedBox>
              </SimpleGrid>

              <Text fontSize="xs" color="gray.400" textAlign="center" fontStyle="italic" maxW="700px">
                We eat our own dog food. JunkDrop's market position is the result of running this exact analysis
                on Nashville junk removal in 2020 and acting on what it found.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 9.8: OBJECTION HANDLER — "Can't I just Google this?"
            (Premium dark surface so it feels like a confident answer,
            not a defensive footnote.)
            ======================================================== */}
        <Box
          py={{ base: 14, lg: 20 }}
          bg="linear-gradient(135deg, #0a0e1a 0%, #1a1a2e 50%, #0f1d3a 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0.5} size="60px" position={{ top: "10%", right: "8%" }} opacity={0.08}>
            <Box bg="white" borderRadius="full" w="60px" h="60px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1.2} size="40px" position={{ bottom: "12%", left: "5%" }} opacity={0.08}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <Container maxW="900px" position="relative" zIndex={1}>
            <VStack spacing={8} textAlign="center">
              <AnimatedBox animation="fadeIn" delay={0.2}>
                <Badge bg="whiteAlpha.200" color="white" px={3} py={1} borderRadius="full" fontSize="sm" border="1px solid" borderColor="whiteAlpha.300">
                  The Honest Question
                </Badge>
              </AnimatedBox>
              <AnimatedBox animation="slideUp" delay={0.3}>
                <Heading
                  size={{ base: "xl", md: "2xl" }}
                  color="white"
                  lineHeight={1.2}
                >
                  &ldquo;Can't I just Google this myself?&rdquo;
                </Heading>
              </AnimatedBox>

              <AnimatedBox animation="fadeIn" delay={0.4}>
                <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" lineHeight="tall" maxW="780px">
                  You can. It'll take you{" "}
                  <Text as="span" color="white" fontWeight="bold">20-40 hours per market</Text>, you'll
                  miss <Text as="span" color="white" fontWeight="bold">60% of the signal</Text>, and you'll
                  never see what's behind paid traffic, ad creatives, or review velocity trends.
                </Text>
              </AnimatedBox>

              {/* Stats bar */}
              <AnimatedBox animation="slideUp" delay={0.5} w="full">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} w="full">
                  {[
                    { num: "11", unit: "data sources", label: "We pull from" },
                    { num: "4", unit: "different analyses", label: "We run" },
                    { num: "1", unit: "private login dashboard", label: "We deliver" },
                  ].map((s, i) => (
                    <VStack
                      key={i}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      borderRadius="2xl"
                      backdropFilter="blur(10px)"
                      px={5}
                      py={6}
                      spacing={1}
                    >
                      <Text fontSize="xs" color="whiteAlpha.700" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                        {s.label}
                      </Text>
                      <HStack align="baseline" spacing={2}>
                        <Text
                          fontSize="5xl"
                          fontWeight="extrabold"
                          color="white"
                          lineHeight={1}
                          bgGradient="linear(to-r, #00d2ff, #3a7bd5)"
                          bgClip="text"
                        >
                          {s.num}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.800" fontWeight="semibold">
                          {s.unit}
                        </Text>
                      </HStack>
                    </VStack>
                  ))}
                </SimpleGrid>
              </AnimatedBox>

              <AnimatedBox animation="fadeIn" delay={0.6}>
                <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" lineHeight="tall" maxW="780px">
                  The <Text as="span" color="white" fontWeight="bold">${REPORT_PRICE_USD}</Text> isn't for the
                  data &mdash; it's for the{" "}
                  <Text as="span" color="white" fontWeight="bold">30 hours of your life you get back</Text>,
                  and the certainty of seeing what you'd otherwise miss.
                </Text>
              </AnimatedBox>

              <AnimatedBox animation="fadeIn" delay={0.7}>
                <Button
                  size="lg"
                  bg="white"
                  color="#0066FF"
                  rightIcon={<FaArrowRight />}
                  onClick={handleGetReport}
                  px={10}
                  fontWeight="bold"
                  borderRadius="full"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 12px 40px rgba(255,255,255,0.2)", bg: "gray.100" }}
                >
                  Get My Private Dashboard &mdash; ${REPORT_PRICE_USD}
                </Button>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 10: WHY GENERIC RESEARCH IS NOT ENOUGH
            (Comparison table — keeps the existing matrix style.)
            ======================================================== */}
        <Box py={{ base: 12, lg: 16 }} bg="white">
          <Container maxW="90vw">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="1000px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                    Why This vs Free Research
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Why Generic Research Is Not Enough</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.600">
                    A side-by-side look at what you actually get from each approach to pre-launch market research.
                  </Text>
                </AnimatedBox>
              </VStack>

              {!isMobile && (
                <AnimatedBox animation="slideUp" delay={0.5} w="full">
                  <Box bg="white" borderRadius="2xl" boxShadow="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                    <Table variant="simple" size="md">
                      <Thead>
                        <Tr bg="gray.50">
                          <Th py={3.5} px={6} fontSize="sm" fontWeight="semibold" color="gray.600" borderBottomWidth="2px" borderColor="gray.200" w="32%" textTransform="uppercase" letterSpacing="wider">
                            Capability
                          </Th>
                          <Th py={3.5} px={4} fontSize="sm" fontWeight="semibold" color="gray.600" borderBottomWidth="2px" borderColor="gray.200" textAlign="center" w="17%" textTransform="uppercase" letterSpacing="wider">
                            Google Search
                          </Th>
                          <Th py={3.5} px={4} fontSize="sm" fontWeight="semibold" color="gray.600" borderBottomWidth="2px" borderColor="gray.200" textAlign="center" w="17%" textTransform="uppercase" letterSpacing="wider">
                            YouTube Advice
                          </Th>
                          <Th py={3.5} px={4} fontSize="sm" fontWeight="semibold" color="gray.600" borderBottomWidth="2px" borderColor="gray.200" textAlign="center" w="17%" textTransform="uppercase" letterSpacing="wider">
                            Generic Reports
                          </Th>
                          <Th py={3.5} px={4} fontSize="sm" fontWeight="bold" color="blue.600" bg="blue.50" borderBottomWidth="2px" borderColor="blue.200" textAlign="center" w="17%" textTransform="uppercase" letterSpacing="wider">
                            Localytics Dashboard
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {COMPARISON_FEATURES.map((row, index) => (
                          <Tr key={index} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                            <Td py={3} px={6} fontWeight="medium" color="gray.700" borderColor="gray.100" fontSize="sm">{row.feature}</Td>
                            <Td py={3} px={4} textAlign="center" borderColor="gray.100"><ComparisonCell value={row.google} /></Td>
                            <Td py={3} px={4} textAlign="center" borderColor="gray.100"><ComparisonCell value={row.youtube} /></Td>
                            <Td py={3} px={4} textAlign="center" borderColor="gray.100"><ComparisonCell value={row.generic} /></Td>
                            <Td py={3} px={4} textAlign="center" bg="blue.50" borderColor="blue.100"><ComparisonCell value={row.localytics} /></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </AnimatedBox>
              )}

              {isMobile && (
                <AnimatedBox animation="slideUp" delay={0.5} w="full">
                  {COMPARISON_FEATURES.map((row, index) => (
                    <MobileComparisonCard key={index} row={row} />
                  ))}
                </AnimatedBox>
              )}

              <Text fontSize="xs" color="gray.400" textAlign="center" fontStyle="italic">
                Free sources give you fragments. Your private Localytics dashboard is built specifically for your city + niche.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 10.5: SOCIAL PROOF — real Localytics operators
            (Moved here from position 3 so cold traffic builds belief
            from the founder story / proof first, then lands social
            proof right before the buy button. Framed honestly as
            "operators using Localytics" rather than $297 private-
            dashboard buyers, since those are the same product family
            but not identical SKUs.)
            ======================================================== */}
        <Box py={{ base: 12, lg: 14 }} bg="gray.50" overflow="hidden">
          <VStack spacing={3} mb={8} px={4} textAlign="center">
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
              Real Operators
            </Badge>
            <Heading size={{ base: "md", md: "lg" }} color="gray.800" maxW="720px">
              How operators use Localytics to outrank their local competitors
            </Heading>
            <Text fontSize={{ base: "sm", md: "md" }} color="gray.500" maxW="640px">
              These quotes are from operators using the Localytics platform — the same competitive intelligence that powers your Private Market Intelligence Dashboard.
            </Text>
          </VStack>

          <Box
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "120px",
              height: "100%",
              background: "linear-gradient(to right, var(--chakra-colors-gray-50), transparent)",
              zIndex: 2,
              pointerEvents: "none",
            }}
            _after={{
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "120px",
              height: "100%",
              background: "linear-gradient(to left, var(--chakra-colors-gray-50), transparent)",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <Flex
              animation={`${scrollMarquee} 40s linear infinite`}
              w="fit-content"
              gap={{ base: 5, md: 6 }}
              _hover={{ animationPlayState: "paused" }}
            >
              {[...CASE_STUDIES, ...CASE_STUDIES].map((story, index) => (
                <Box
                  key={index}
                  minW={{ base: "340px", md: "420px", lg: "480px" }}
                  maxW={{ base: "340px", md: "420px", lg: "480px" }}
                  p={{ base: 5, md: 7 }}
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="gray.100"
                  flexShrink={0}
                >
                  <VStack align="start" spacing={4}>
                    <HStack spacing={4} w="full">
                      <Image
                        src={story.avatar}
                        alt={`${story.name} headshot`}
                        boxSize={{ base: "56px", md: "64px" }}
                        borderRadius="full"
                        objectFit="cover"
                        border="2px solid"
                        borderColor="gray.100"
                      />
                      <VStack align="start" spacing={0} flex="1" minW={0}>
                        <Text fontWeight="bold" color="gray.800" fontSize={{ base: "sm", md: "md" }}>
                          {story.name}
                        </Text>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                          {story.role} &middot; {story.company}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={0.5}>
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <Icon key={starIndex} as={FaStar} color="yellow.400" boxSize={{ base: 3, md: 3.5 }} />
                      ))}
                    </HStack>
                    <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" lineHeight="tall">
                      &ldquo;{story.quote}&rdquo;
                    </Text>
                    <Button
                      size="sm"
                      variant="link"
                      color="#0066FF"
                      rightIcon={<FaArrowRight />}
                      fontWeight="semibold"
                      _hover={{ color: "#0052CC", textDecoration: "none" }}
                      onClick={handleGetReport}
                    >
                      Get my Private Dashboard
                    </Button>
                  </VStack>
                </Box>
              ))}
            </Flex>
          </Box>
        </Box>

        {/* ========================================================
            SECTION 11: PRICING — $297 one-time
            ======================================================== */}
        <Box ref={offerRef} py={{ base: 14, lg: 20 }} bg="gray.50" id="offer">
          <Container maxW="85vw">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">Pricing</Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Private Market Intelligence Dashboard</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.500">
                    One city. One business idea. Up to 20 competitors loaded into your private login dashboard.
                  </Text>
                </AnimatedBox>
              </VStack>

              <AnimatedBox animation="slideUp" delay={0.3} w="full" maxW="560px">
                <Box
                  p={{ base: 5, md: 8 }}
                  bg="white"
                  borderRadius="2xl"
                  border="2px solid"
                  borderColor="blue.500"
                  position="relative"
                  boxShadow="xl"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-6px)", boxShadow: "2xl" }}
                >
                  <Badge
                    position="absolute"
                    top={-3}
                    left="50%"
                    transform="translateX(-50%)"
                    bg="linear-gradient(90deg, #B8860B 0%, #FFD700 50%, #B8860B 100%)"
                    color="gray.900"
                    px={4}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="extrabold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    boxShadow="0 4px 14px rgba(184, 134, 11, 0.45)"
                  >
                    Founder's Beta &middot; First {FOUNDER_BETA_SEATS_TOTAL} Dashboards
                  </Badge>

                  <VStack spacing={5} align="stretch">
                    <VStack align="center" spacing={1}>
                      <Heading size="md" color="gray.800" textAlign="center">{REPORT_OFFER.name}</Heading>
                      <Text fontSize="sm" color="gray.500" textAlign="center">{REPORT_OFFER.tagline}</Text>
                    </VStack>

                    <VStack spacing={2}>
                      <HStack align="baseline" spacing={3} justify="center" flexWrap="wrap">
                        <Text
                          fontSize="2xl"
                          fontWeight="semibold"
                          color="gray.400"
                          lineHeight={1}
                          textDecoration="line-through"
                          textDecorationColor="red.300"
                          textDecorationThickness="2px"
                        >
                          ${REPORT_LIST_PRICE_USD}
                        </Text>
                        <Text fontSize="6xl" fontWeight="bold" color="gray.800" lineHeight={1}>
                          ${REPORT_OFFER.price}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" textAlign="center">
                        one-time, no subscription &middot;{" "}
                        <Text as="span" color="green.600" fontWeight="bold">
                          ${REPORT_LIST_PRICE_USD - REPORT_OFFER.price} off list price
                        </Text>
                      </Text>
                      <HStack spacing={2} pt={2} bg="red.50" border="1px solid" borderColor="red.200" px={3} py={2} borderRadius="full">
                        <Icon as={FaExclamationTriangle} color="red.500" boxSize={3.5} />
                        <Text mb={0} fontSize="sm" color="red.700" fontWeight="semibold" textAlign="center">
                          Only {FOUNDER_BETA_SEATS_REMAINING} of {FOUNDER_BETA_SEATS_TOTAL} Founder's Beta seats left
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" textAlign="center" maxW="420px">
                        After the first {FOUNDER_BETA_SEATS_TOTAL} dashboards, pricing returns to ${REPORT_LIST_PRICE_USD}.
                      </Text>
                      <Text fontSize="sm" color="gray.500" textAlign="center" pt={1}>
                        Your private dashboard includes:
                      </Text>
                      <HStack spacing={1.5} pt={1}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={3.5} />
                        <Text mb={0} fontSize="sm" color="green.600" fontWeight="semibold">
                          100% refund if delivered late or factually wrong
                        </Text>
                      </HStack>
                    </VStack>

                    <Divider />

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2.5}>
                      {REPORT_OFFER.features.map((feature, featureIndex) => (
                        <HStack key={featureIndex} align="start" spacing={2.5}>
                          <Flex w="18px" h="18px" bg="green.100" borderRadius="full" align="center" justify="center" flexShrink={0} mt={0.5}>
                            <Icon as={FaCheck} color="green.500" boxSize={2} />
                          </Flex>
                          <Text fontSize="sm" color="gray.600">{feature}</Text>
                        </HStack>
                      ))}
                    </SimpleGrid>

                    {/* $197 add-on for a second city or second niche.
                        Surfaced inside the pricing card (not buried below it)
                        so the AOV-bumping option is visible at decision time. */}
                    <Box
                      bg="blue.50"
                      border="1px solid"
                      borderColor="blue.200"
                      borderRadius="xl"
                      px={4}
                      py={3.5}
                    >
                      <HStack spacing={3} align="start">
                        <Flex
                          w="28px"
                          h="28px"
                          bg="blue.500"
                          color="white"
                          borderRadius="full"
                          align="center"
                          justify="center"
                          flexShrink={0}
                          mt={0.5}
                          fontWeight="extrabold"
                          fontSize="sm"
                        >
                          +
                        </Flex>
                        <VStack align="start" spacing={1} flex="1">
                          <HStack spacing={2} align="baseline" flexWrap="wrap">
                            <Text fontSize="sm" fontWeight="bold" color="gray.900">
                              Add a second city or second niche
                            </Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.600">
                              +${SECOND_MARKET_ADDON_USD}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600" lineHeight={1.5}>
                            Comparing two markets before you commit? Get a second full private dashboard so you can put them side by side. Select this option at checkout.
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>

                    <Button
                      w="full"
                      size="lg"
                      colorScheme="blue"
                      borderRadius="xl"
                      fontWeight="bold"
                      py={6}
                      rightIcon={<FaArrowRight />}
                      _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                      onClick={handleGetReport}
                    >
                      {REPORT_OFFER.cta} — ${REPORT_OFFER.price}
                    </Button>
                    <VStack spacing={1.5} w="full">
                      <Text fontSize="xs" color="gray.500" textAlign="center" maxW="420px">
                        A private market intelligence dashboard built for your city and business idea. Login delivered in {DASHBOARD_DELIVERY_WINDOW}. No subscription.
                      </Text>
                      <HStack spacing={1.5} justify="center" w="full">
                        <Icon as={FaLock} color="gray.400" boxSize={3} />
                        <Text mb={0} fontSize="xs" color="gray.400">
                          Secure checkout · 100% money-back if we can't deliver
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 12: FAQ
            ======================================================== */}
        <Box py={{ base: 12, lg: 16 }} bg="white" id="faq">
          <Container maxW="container.lg">
            <VStack spacing={10}>
              <VStack spacing={3} textAlign="center" maxW="700px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="sm">
                    FAQ
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Frequently Asked Questions</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.500">
                    Everything you need to know about your private market intelligence dashboard.
                  </Text>
                </AnimatedBox>
              </VStack>

              <VStack spacing={3} w="full" align="stretch">
                {FAQ_ITEMS.map((faq, index) => (
                  <AnimatedBox key={index} animation="slideUp" delay={0.2 + index * 0.05}>
                    <Box
                      bg="white"
                      borderRadius="xl"
                      border="2px solid"
                      borderColor={openFaqIndex === index ? "blue.500" : "gray.200"}
                      overflow="hidden"
                      transition="all 0.3s"
                      _hover={{ borderColor: "blue.300", boxShadow: "md" }}
                    >
                      <Button
                        w="full"
                        h="auto"
                        py={5}
                        px={6}
                        bg="transparent"
                        _hover={{ bg: "gray.50" }}
                        _active={{ bg: "gray.100" }}
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        borderRadius="none"
                        textAlign="left"
                        fontWeight="normal"
                      >
                        <HStack w="full" justify="space-between" align="start" spacing={4}>
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontWeight="semibold" color="gray.800" fontSize={{ base: "md", md: "lg" }} lineHeight="tall">
                              {faq.question}
                            </Text>
                          </VStack>
                          <Flex
                            w="32px"
                            h="32px"
                            bg={openFaqIndex === index ? "blue.500" : "gray.100"}
                            borderRadius="full"
                            align="center"
                            justify="center"
                            flexShrink={0}
                            transition="all 0.3s"
                          >
                            <Icon
                              as={openFaqIndex === index ? FaChevronUp : FaChevronDown}
                              color={openFaqIndex === index ? "white" : "gray.600"}
                              boxSize={4}
                            />
                          </Flex>
                        </HStack>
                      </Button>
                      <Box
                        maxH={openFaqIndex === index ? "500px" : "0"}
                        overflow="hidden"
                        transition="max-height 0.3s ease-in-out"
                      >
                        <Box px={6} pb={5} pt={0}>
                          <Text color="gray.600" fontSize={{ base: "sm", md: "md" }} lineHeight="tall">
                            {faq.answer}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  </AnimatedBox>
                ))}
              </VStack>

              <AnimatedBox animation="fadeIn" delay={0.7} w="full">
                <Box
                  bgGradient="linear(to-r, blue.50, purple.50)"
                  borderRadius="xl"
                  p={6}
                  textAlign="center"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <VStack spacing={3}>
                    <Flex w={12} h={12} bg="blue.100" borderRadius="full" align="center" justify="center" mx="auto">
                      <Icon as={FaQuestionCircle} color="blue.600" boxSize={6} />
                    </Flex>
                    <Heading size="md" color="gray.800">Still have questions?</Heading>
                    <Text color="gray.600" fontSize="md" maxW="540px">
                      Stop guessing what your market looks like. Get the actual numbers — top competitors, traffic, ads, and AI SWOT for your city + niche.
                    </Text>
                    <Button
                      size="lg"
                      bg="#0066FF"
                      color="white"
                      rightIcon={<FaArrowRight />}
                      onClick={handleGetReport}
                      fontWeight="bold"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                    >
                      Get My Private Dashboard
                    </Button>
                  </VStack>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 13: FINAL CTA — "Don't Start Blind."
            ======================================================== */}
        <Box
          py={{ base: 12, lg: 16 }}
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0} size="100px" position={{ top: "5%", left: "3%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="100px" h="100px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1.5} size="80px" position={{ bottom: "10%", right: "5%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="80px" h="80px" />
          </FloatingElement>

          <Container maxW="80vw" position="relative" zIndex={1}>
            <VStack spacing={6} textAlign="center">
              <AnimatedBox animation="slideUp" delay={0.2}>
                <Heading size={{ base: "xl", md: "2xl" }} color="white" lineHeight={1.2}>
                  Don't Start Blind.
                </Heading>
              </AnimatedBox>
              <AnimatedBox animation="fadeIn" delay={0.4}>
                <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" maxW="640px">
                  Before you spend thousands on equipment, ads, rent, hiring, or branding,
                  see what the market actually looks like.
                </Text>
              </AnimatedBox>
              <AnimatedBox animation="scale" delay={0.6}>
                <Button
                  size="lg"
                  bg="white"
                  color="#0066FF"
                  rightIcon={<FaArrowRight />}
                  px={10}
                  fontSize="md"
                  fontWeight="bold"
                  _hover={{ transform: "translateY(-3px)", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}
                  transition="all 0.3s"
                  onClick={handleGetReport}
                >
                  Get My Private Dashboard
                </Button>
              </AnimatedBox>
              <AnimatedBox animation="fadeIn" delay={0.8}>
                <HStack spacing={6} pt={2} flexWrap="wrap" justify="center">
                  <HStack>
                    <Icon as={FaCheckCircle} color="green.300" />
                    <Text mb={0} color="whiteAlpha.900" fontSize="sm">${REPORT_PRICE_USD} one-time, no subscription</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheckCircle} color="green.300" />
                    <Text mb={0} color="whiteAlpha.900" fontSize="sm">Login delivered in {DASHBOARD_DELIVERY_WINDOW}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheckCircle} color="green.300" />
                    <Text mb={0} color="whiteAlpha.900" fontSize="sm">Private dashboard you can sign in to anytime</Text>
                  </HStack>
                </HStack>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ========================================================
            SECTION 14: FOOTER (slim, no SaaS links)
            ======================================================== */}
        <Box py={6} bg="gray.900">
          <Container maxW="90vw">
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "center", md: "center" }}
              gap={4}
            >
              <Image src="/output.png" alt="Logo" h="36px" w="auto" objectFit="contain" />
              <Box textAlign={{ base: "center", md: "right" }}>
                <Text color="gray.500" fontSize="xs">
                  &copy; {new Date().getFullYear()} Localytics. All rights reserved.
                </Text>
                <Text color="gray.600" fontSize="xs" mt={1}>
                  Started, Owned and Operated in Nashville, TN.
                </Text>
              </Box>
            </Flex>
          </Container>
        </Box>
      </Box>

      {/* ========================================================
          MARKET REPORT MODAL
          Loading animation -> Lead capture form -> Success
          ======================================================== */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        isCentered
        size={{ base: "md", md: "xl" }}
        motionPreset="slideInBottom"
        closeOnOverlayClick={!isAnalyzing && !showEligible}
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="2xl"
          mx={4}
          overflow="hidden"
          bg="white"
          boxShadow="0 25px 80px rgba(0, 0, 0, 0.3)"
        >
          {!isAnalyzing && !showEligible && <ModalCloseButton zIndex={10} size="lg" />}
          <ModalBody p={0}>
            {isAnalyzing ? (
              <Box
                bg="linear-gradient(160deg, #f8faff 0%, #eef2ff 40%, #f0f7ff 100%)"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  opacity={0.4}
                  background="linear-gradient(90deg, transparent 0%, rgba(0,102,255,0.04) 50%, transparent 100%)"
                  backgroundSize="200% 100%"
                  animation={`${shimmer} 3s ease-in-out infinite`}
                  pointerEvents="none"
                />

                <VStack spacing={8} py={{ base: 12, md: 16 }} px={{ base: 6, md: 12 }} position="relative" zIndex={1}>
                  <Box position="relative" animation={`${pulseGlow} 2.5s ease-in-out infinite`} borderRadius="full">
                    <Box
                      position="absolute"
                      top="-12px"
                      left="-12px"
                      right="-12px"
                      bottom="-12px"
                      borderRadius="full"
                      border="2px dashed"
                      borderColor="blue.200"
                      opacity={0.5}
                      animation={`${orbitSpin} 8s linear infinite`}
                    />
                    <Box
                      position="absolute"
                      top="-22px"
                      left="-22px"
                      right="-22px"
                      bottom="-22px"
                      borderRadius="full"
                      border="1px dashed"
                      borderColor="blue.100"
                      opacity={0.3}
                      animation={`${orbitSpin} 12s linear infinite reverse`}
                    />
                    <CircularProgress
                      value={analysisProgress}
                      size={{ base: "110px", md: "130px" }}
                      thickness="5px"
                      color="blue.500"
                      trackColor="gray.100"
                      capIsRound
                    >
                      <CircularProgressLabel>
                        <VStack spacing={0}>
                          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="gray.800" lineHeight={1}>
                            {Math.round(analysisProgress)}%
                          </Text>
                        </VStack>
                      </CircularProgressLabel>
                    </CircularProgress>
                  </Box>

                  <VStack spacing={1}>
                    <Heading size="md" color="gray.800" textAlign="center">
                      Building your private dashboard
                    </Heading>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {niche || "your niche"} · {city || "your city"}
                    </Text>
                  </VStack>

                  <VStack spacing={2.5} w="full" maxW="380px">
                    {ANALYSIS_STEPS.map((step, index) => {
                      const isActive = index === analysisStep;
                      const isComplete = index < analysisStep || (index === ANALYSIS_STEPS.length - 1 && analysisProgress >= 100);
                      const isUpcoming = index > analysisStep;
                      return (
                        <Box
                          key={index}
                          w="full"
                          opacity={isUpcoming ? 0.3 : 1}
                          animation={isActive ? `${fadeInStep} 0.5s ease-out` : undefined}
                          transition="all 0.4s ease"
                        >
                          <HStack spacing={3} w="full">
                            <Flex
                              w="28px"
                              h="28px"
                              borderRadius="full"
                              align="center"
                              justify="center"
                              flexShrink={0}
                              bg={isComplete ? "green.50" : isActive ? "blue.50" : "gray.50"}
                              border="2px solid"
                              borderColor={isComplete ? "green.400" : isActive ? "blue.400" : "gray.200"}
                              transition="all 0.4s"
                            >
                              {isComplete ? (
                                <Icon as={FaCheck} color="green.500" boxSize={3} />
                              ) : isActive ? (
                                <Box
                                  w="8px"
                                  h="8px"
                                  borderRadius="full"
                                  bg="blue.500"
                                  animation={`${pulseGlow} 1.5s ease-in-out infinite`}
                                />
                              ) : (
                                <Box w="6px" h="6px" borderRadius="full" bg="gray.300" />
                              )}
                            </Flex>
                            <VStack align="start" spacing={0} flex={1}>
                              <Text
                                fontSize="sm"
                                fontWeight={isActive ? "semibold" : "medium"}
                                color={isActive ? "gray.800" : isComplete ? "gray.600" : "gray.400"}
                              >
                                {step.label}
                              </Text>
                              {(isActive || isComplete) && step.sub && (
                                <Text
                                  fontSize="xs"
                                  color={isActive ? "blue.500" : "gray.400"}
                                  animation={isActive ? `${fadeInStep} 0.6s ease-out` : undefined}
                                >
                                  {step.sub}
                                </Text>
                              )}
                            </VStack>
                            {isActive && (
                              <Box
                                w="36px"
                                h="3px"
                                borderRadius="full"
                                overflow="hidden"
                                bg="gray.100"
                                flexShrink={0}
                              >
                                <Box
                                  h="full"
                                  borderRadius="full"
                                  bg="blue.400"
                                  animation={`${shimmer} 1.5s ease-in-out infinite`}
                                  backgroundSize="200% 100%"
                                  backgroundImage="linear-gradient(90deg, transparent, #3b82f6, transparent)"
                                  w="full"
                                />
                              </Box>
                            )}
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>

                  <HStack spacing={2} pt={2}>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.400" animation={`${pulseGlow} 1s ease-in-out infinite`} />
                    <Text fontSize="xs" color="gray.400">
                      This usually takes a few seconds
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            ) : showEligible ? (
              <Box position="relative" overflow="hidden" bg="white">
                <VStack spacing={5} py={{ base: 12, md: 16 }} px={{ base: 6, md: 10 }} align="center" position="relative" zIndex={1}>
                  <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" pointerEvents="none" zIndex={0}>
                    {CONFETTI_PIECES.map((piece, i) => (
                      <Box
                        key={i}
                        position="absolute"
                        top="0"
                        left="0"
                        style={{
                          '--cx': `${piece.cx}px`,
                          '--cy': `${piece.cy}px`,
                        }}
                        animation={`${confettiBurst} 1.4s ${piece.delay}s ease-out forwards`}
                        opacity={0}
                      >
                        {piece.shape === 0 ? (
                          <Box w={`${piece.size}px`} h={`${piece.size}px`} borderRadius="full" bg={piece.color} />
                        ) : piece.shape === 1 ? (
                          <Box w={`${piece.size}px`} h={`${piece.size * 0.6}px`} borderRadius="sm" bg={piece.color} transform="rotate(45deg)" />
                        ) : (
                          <Box
                            w="0" h="0"
                            borderLeft={`${piece.size / 2}px solid transparent`}
                            borderRight={`${piece.size / 2}px solid transparent`}
                            borderBottom={`${piece.size}px solid ${piece.color}`}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Box animation={`${scaleIn} 0.5s ease-out`} position="relative" zIndex={1}>
                    <Flex
                      w={20}
                      h={20}
                      bg="green.50"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      border="3px solid"
                      borderColor="green.400"
                    >
                      <Icon as={FaCheckCircle} w={10} h={10} color="green.500" />
                    </Flex>
                  </Box>

                  <VStack spacing={2} animation={`${fadeInStep} 0.6s 0.2s ease-out both`} position="relative" zIndex={1}>
                    <Heading size="lg" color="gray.800" textAlign="center" lineHeight={1.3}>
                      Your market is ready to be analyzed.
                    </Heading>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      Just a few details and we'll start building your private dashboard.
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            ) : showSuccess ? (
              <VStack spacing={5} align="center" py={{ base: 8, md: 10 }} px={{ base: 6, md: 10 }}>
                <Flex w={16} h={16} bg="green.100" borderRadius="full" align="center" justify="center">
                  <Icon as={FaCheckCircle} w={8} h={8} color="green.500" />
                </Flex>
                <Heading as="h2" size="lg" textAlign="center" color="gray.800">
                  Dashboard Request Received!
                </Heading>
                <Text fontSize="md" textAlign="center" color="gray.600" maxW="440px">
                  Thanks, {name}. We're building your private dashboard for {niche || "your niche"} in {city || "your city"}.
                </Text>
                <Box bg="blue.50" p={4} borderRadius="xl" w="full">
                  <VStack spacing={2} align="start">
                    <HStack spacing={2}>
                      <Icon as={FaLock} color="blue.500" boxSize={4} />
                      <Text fontWeight="bold" color="gray.800">What happens next</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Our team will personally load up to 20 competitors for {city || "your city"} into your private dashboard and email your login in {DASHBOARD_DELIVERY_WINDOW}.
                      We'll reach out at <Text as="span" fontWeight="semibold">{email}</Text> to confirm payment and any clarifying details.
                    </Text>
                  </VStack>
                </Box>
                <Button
                  bg="#0066FF"
                  color="white"
                  onClick={() => {
                    setShowSuccess(false);
                    setName('');
                    setEmail('');
                    setPhone('');
                    setSmsConsent(false);
                    handleCloseModal();
                  }}
                  size="md"
                  fontWeight="semibold"
                  _hover={{ bg: "#0052CC", transform: "translateY(-2px)" }}
                >
                  Done
                </Button>
              </VStack>
            ) : (
              <Box py={{ base: 6, md: 8 }} px={{ base: 6, md: 10 }}>
                <form onSubmit={handleSubmit}>
                  <VStack spacing={5} align="start">
                    <VStack spacing={1} align="start" w="full">
                      <Heading as="h2" size="lg" color="gray.900" lineHeight={1.2}>
                        One step from your private dashboard
                      </Heading>
                      <Text fontSize="sm" color="gray.500" lineHeight={1.5}>
                        We'll load every competitor into your private dashboard &mdash; login delivered in {DASHBOARD_DELIVERY_WINDOW}.
                      </Text>
                    </VStack>

                    {/* Green "Your Market" pill — mirrors the URL pill in the
                        homepage modal screenshot. Uses the city + business idea
                        the buyer entered in the hero form. */}
                    <Box w="full">
                      <FormLabel
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        color="gray.700"
                        mb={1.5}
                      >
                        Your Market
                      </FormLabel>
                      <HStack
                        bg="green.50"
                        border="1px solid"
                        borderColor="green.200"
                        borderRadius="xl"
                        px={4}
                        py={3}
                        spacing={3}
                        align="center"
                      >
                        <Icon as={FaCheckCircle} color="green.400" boxSize={4} flexShrink={0} />
                        <Text fontSize="md" color="gray.800" fontWeight="medium" noOfLines={1} flex={1}>
                          {(city.trim() || niche.trim()) ? (
                            <>
                              {niche.trim() || "your business idea"}
                              <Text as="span" color="gray.400" px={2}>·</Text>
                              {city.trim() || "your city"}
                            </>
                          ) : (
                            "Tell us your city + business idea"
                          )}
                        </Text>
                      </HStack>
                    </Box>

                    {/* Inline editable City + Business Idea, only if missing */}
                    {(!city.trim() || !niche.trim()) && (
                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
                        <FormControl isRequired>
                          <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="gray.700" mb={1.5}>
                            City
                          </FormLabel>
                          <AutocompleteInput
                            value={city}
                            onChange={(e) => { setCity(e.target.value); setError(''); }}
                            getSuggestions={searchCities}
                            inputProps={{
                              size: "lg",
                              placeholder: "Nashville, TN",
                              bg: "white",
                              borderColor: "gray.200",
                              _hover: { borderColor: "gray.300" },
                              _focus: { borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" },
                              borderRadius: "xl",
                            }}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="gray.700" mb={1.5}>
                            Business Idea
                          </FormLabel>
                          <AutocompleteInput
                            value={niche}
                            onChange={(e) => { setNiche(e.target.value); setError(''); }}
                            getSuggestions={searchBusinessTypes}
                            inputProps={{
                              size: "lg",
                              placeholder: "Junk removal, med spa...",
                              bg: "white",
                              borderColor: "gray.200",
                              _hover: { borderColor: "gray.300" },
                              _focus: { borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" },
                              borderRadius: "xl",
                            }}
                          />
                        </FormControl>
                      </SimpleGrid>
                    )}

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="gray.700" mb={1.5}>
                          Your Name
                        </FormLabel>
                        <Input
                          size="lg"
                          placeholder="Walter"
                          value={name}
                          onChange={(e) => { setName(e.target.value); setError(''); }}
                          bg="white"
                          borderColor={error && !name.trim() ? "red.400" : "gray.200"}
                          _hover={{ borderColor: "gray.300" }}
                          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                          borderRadius="xl"
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="gray.700" mb={1.5}>
                          Email
                        </FormLabel>
                        <Input
                          size="lg"
                          placeholder="you@email.com"
                          type="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); setError(''); }}
                          bg="white"
                          borderColor={error && (!email || !email.includes('@')) ? "red.400" : "gray.200"}
                          _hover={{ borderColor: "gray.300" }}
                          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                          borderRadius="xl"
                        />
                      </FormControl>
                    </SimpleGrid>

                    <FormControl>
                      <FormLabel htmlFor="mr-phone" fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="gray.700" mb={1.5}>
                        Mobile (optional)
                      </FormLabel>
                      <Input
                        id="mr-phone"
                        size="lg"
                        placeholder="(615) 555-0123"
                        type="tel"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); setError(''); }}
                        bg="white"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        borderRadius="xl"
                      />
                      <Checkbox
                        mt={3}
                        alignItems="flex-start"
                        isChecked={smsConsent}
                        onChange={(e) => { setSmsConsent(e.target.checked); setError(''); }}
                      >
                        <Text fontSize="xs" color="gray.600">{SMS_CONSENT_TEXT}</Text>
                      </Checkbox>
                    </FormControl>

                    {error && <Text color="red.500" fontSize="sm" w="full">{error}</Text>}

                    {(() => {
                      const isFormReady = name.trim() && email && email.includes('@') && city.trim() && niche.trim();
                      return (
                        <Button
                          type="submit"
                          size="lg"
                          w="full"
                          color={isFormReady ? "white" : "gray.800"}
                          rightIcon={<Text as="span" fontSize="lg">→</Text>}
                          isDisabled={!isFormReady}
                          isLoading={isLoading}
                          loadingText="Redirecting to checkout..."
                          borderRadius="xl"
                          fontWeight="bold"
                          py={6}
                          _hover={isFormReady
                            ? { transform: "translateY(-2px)", filter: "brightness(1.1)" }
                            : { transform: "translateY(-1px)", boxShadow: "md", bg: "#DDD0B8" }
                          }
                          _active={{ filter: "brightness(0.9)" }}
                          sx={isFormReady ? {
                            background: "linear-gradient(90deg, #7B5800 0%, #B8860B 12%, #DAA520 25%, #FFD700 38%, #FFFACD 50%, #FFD700 62%, #DAA520 75%, #B8860B 88%, #7B5800 100%)",
                            backgroundSize: "200% auto",
                            animation: `${goldShimmer} 2s linear infinite, ${goldPulse} 1.5s ease-in-out infinite`,
                            transition: "color 0.4s ease, box-shadow 0.4s ease",
                            border: "1px solid rgba(255, 215, 0, 0.6)",
                          } : {
                            background: "#E8DCC8",
                            transition: "background 0.4s ease, color 0.4s ease",
                          }}
                        >
                          Get my Private Dashboard &mdash; ${REPORT_PRICE_USD}
                        </Button>
                      );
                    })()}

                    <VStack spacing={1} w="full" pt={1}>
                      <HStack spacing={1} justify="center" w="full" flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">${REPORT_PRICE_USD} one-time</Text>
                        <Text fontSize="sm" color="gray.400">&middot; no subscription &middot; login in {DASHBOARD_DELIVERY_WINDOW}</Text>
                      </HStack>
                      <HStack spacing={1.5} justify="center" w="full">
                        <Icon as={FaLock} color="gray.400" boxSize={3} />
                        <Text fontSize="xs" color="gray.400">
                          Secure checkout &middot; powered by Stripe
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </form>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketReportLanding;
